import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom'
import { ArrowLeft, Hash, Zap, User, AlertTriangle, DollarSign, Plus, Save, Check, X, MapPin, Users, FileText, Calendar } from 'lucide-react'
import { lotesService } from '../../services/lotesService'
import clientesService from '../../services/clientesService'
import { comissoesService } from '../../services/comissoesService'
import leiloesService from '../../services/leiloesService'
import leiloeiroService from '../../services/leiloeiroService'
import assessoresService from '../../services/assessoresService'
import { faturamentoService } from '../../services/faturamentoService'
import { gestaoService } from '../../services/gestaoService'
import { meiosPagamentoService } from '../../services/meiosPagamentoService'
import organizadoresService from '../../services/organizadoresService'
import { useToast } from '../../hooks/useToast'
import Toast from '../../components/ui/Toast'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import NovoClienteModal from '../../components/modals/NovoClienteModal'
import NovaComissaoModal from '../../components/modals/NovaComissaoModal'
import DespesasLote from '../../components/lotes/DespesasLote'
import AnimaisCavaloEditor from '../../components/lotes/AnimaisCavaloEditor'
import VendedoresCavaloEditor from '../../components/lotes/VendedoresCavaloEditor'
import CompradoresCavaloEditor from '../../components/lotes/CompradoresCavaloEditor'
import ParcelamentoComissaoModal from '../../components/lotes/ParcelamentoComissaoModal'
import PisteiroSelect from '../../components/PisteiroSelect'
import toast from 'react-hot-toast'
import ContratoUmaVia from '../../components/contratos/ContratoUmaVia'
import ContratoNotaButton from '../../components/contratos/ContratoNotaButton'
import Contrato3ViasButton from '../../components/contratos/Contrato3ViasButton'
import NotaPromissoriaButton from '../../components/contratos/NotaPromissoriaButton'
import NotaCompromissoButton from '../../components/contratos/NotaCompromissoButton'
import FichaLeiloeiroButton from '../../components/contratos/FichaLeiloeiroButton'
import { contem } from '../../utils/texto'

// Máscara de moeda (R$) — digitação livre em centavos, formata com separador de
// milhar e vírgula decimal (padrão brasileiro), ex: "45000" digitado vira "450,00".
function formatarMoedaDigitos(valorDigitado) {
  const digitos = (valorDigitado || '').replace(/\D/g, '')
  if (!digitos) return ''
  const numero = parseInt(digitos, 10) / 100
  return numero.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function numeroParaMoeda(numero) {
  if (numero === null || numero === undefined || numero === '') return ''
  return Number(numero).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function moedaParaNumero(valorMascarado) {
  if (!valorMascarado) return null
  const limpo = valorMascarado.replace(/\./g, '').replace(',', '.')
  const numero = parseFloat(limpo)
  return Number.isNaN(numero) ? null : numero
}

// N parcelas iguais, já "abertas" (1 linha por parcela) — usado nos defaults
// de dadosFinanceiros antes de flattenGrupos (declarada dentro do componente)
// estar disponível.
const parcelasIguais = (n, dias = 30) => Array.from({ length: n }, () => ({ quantidade: 1, peso: 1, dias }))

export default function NovoLoteCavalo() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { id: loteId } = useParams()
  const leilaoIdParam = searchParams.get('leilao')
  const { organizadorAtivo } = useOrganizador()
  const { toasts, success, error, hideToast } = useToast()
  const organizadorId = organizadorAtivo?.id
  const isEditMode = !!loteId

  const steps = isEditMode
    ? [
        { id: 'lote', title: 'Lote', icon: Hash },
        { id: 'animais', title: 'Animais', icon: Zap },
        { id: 'vendedor', title: 'Vendedor', icon: User },
        { id: 'despesas', title: 'Despesas', icon: AlertTriangle },
        { id: 'financeiro', title: 'Financeiro', icon: DollarSign },
      ]
    : [
        { id: 'lote', title: 'Lote', icon: Hash },
        { id: 'animais', title: 'Animais', icon: Zap },
        { id: 'vendedor', title: 'Vendedor', icon: User },
      ]

  const [currentStep, setCurrentStep] = useState(0)
  const [loading, setLoading] = useState(isEditMode)
  const [saving, setSaving] = useState(false)
  // Alterações não salvas — mesmo padrão do lote de gado: mostra "Salvar
  // Alterações" sempre que algo mudar em Lote/Animais/Vendedor (marcado
  // explicitamente via marcarAlterado(), chamado nos onChange dos campos).
  const [dadosAlterados, setDadosAlterados] = useState(false)
  const [lote, setLote] = useState(null)
  const [leilao, setLeilao] = useState(null)
  const [loteSalvoModal, setLoteSalvoModal] = useState(null) // { numero, id } — só no cadastro (não edição)

  const [formData, setFormData] = useState({
    numero_lote: '',
    curral: '',
    valor_minimo_unidade: '',
    observacoes: '',
    vendedor_id: '',
    fazenda_id: '',
    fazenda_ficticia: '',
  })

  const [animais, setAnimais] = useState([])
  const [racas, setRacas] = useState([])
  const [tiposAnimal, setTiposAnimal] = useState([])

  // Vendedor(es) — fluxo cavalo permite múltiplos, cada um com sua %
  const [vendedoresLote, setVendedoresLote] = useState([{ vendedor_id: '', vendedor_nome: '', percentual: 100 }])
  const [showNovoVendedor, setShowNovoVendedor] = useState(false)
  const [showNovoComprador, setShowNovoComprador] = useState(false)
  const [fazendas, setFazendas] = useState([])
  const [usarFazendaFicticia, setUsarFazendaFicticia] = useState(false)

  // Financeiro
  const [comissoes, setComissoes] = useState([])
  const [compradorSelecionado, setCompradorSelecionado] = useState(null)
  const [searchComprador, setSearchComprador] = useState('')
  const [opcoesComprador, setOpcoesComprador] = useState([])
  const [compradorRestritoModal, setCompradorRestritoModal] = useState(null) // cliente restrito pendente de confirmação
  const [fazendasComprador, setFazendasComprador] = useState([])
  const compradorSearchRef = useRef(null)
  // Co-compradores (fluxo cavalo — lote vendido para N compradores em %).
  // Índice 0 = comprador principal, espelhado de compradorSelecionado /
  // dadosFinanceiros.fazenda_comprador_id. Só vira payload quando > 1.
  const [compradoresLote, setCompradoresLote] = useState([])
  // Edição do rateio de compradores DEPOIS de faturado (sem desfazer) — só cavalo
  const [editandoRateio, setEditandoRateio] = useState(false)
  const [salvandoRateio, setSalvandoRateio] = useState(false)
  const [dadosFinanceiros, setDadosFinanceiros] = useState({
    valor_total: '',
    fazenda_comprador_id: '',
    comissao_id: '',
    desconto_tipo: '',
    desconto_percentual: '',
    desconto_valor: '',
    modalidade_pagamento: 'A_VISTA',
    dias_prazo: '',
    // Parcelamento avançado (porte do gado — ver gerarParcelasAutomaticas)
    primeira_parcela_tipo: 'A_VISTA',
    primeira_parcela_dias: '',
    grupos_parcelas: parcelasIguais(3),
    data_segunda_parcela: '',
    intervalo_dias: '30',
    // Quem Vendeu (pisteiro) e Leiloeiro do lote
    quem_vendeu_id: null,
    quem_vendeu_percentual: null,
    leiloeiro_id: null,
    fora_do_leilao: false,
    // Assessor vinculado a este lote (fluxo cavalo) + % configurada neste lote
    assessor_id: null,
    assessor_percentual: null,
  })

  // Valor de venda — gate exigido antes de liberar o formulário financeiro
  // (mesmo padrão do lote de gado)
  const [valorVendaDefinido, setValorVendaDefinido] = useState(false)
  const [showValorVendaModal, setShowValorVendaModal] = useState(false)
  const [valorVendaInput, setValorVendaInput] = useState('')
  const [valorVendaExcecaoPorCabeca, setValorVendaExcecaoPorCabeca] = useState(false)
  const [meiosPagamento, setMeiosPagamento] = useState([])
  const [configuracoesFaturamento, setConfiguracoesFaturamento] = useState(null)
  const valorVendaInputRef = useRef(null)

  // Defesa — vendedor "recompra" o próprio lote (mesmo mecanismo do lote de
  // gado: fatura com comprador = vendedor). O leilão decide se a defesa é por
  // tabela de faixas fixas ou percentual de comissão (Leilao.defesa_modo) —
  // o modal se adapta e mostra um preview em tempo real vindo do backend.
  const [showDefesaModal, setShowDefesaModal] = useState(false)
  const [valorDefesa, setValorDefesa] = useState('')
  const [comissaoDefesaId, setComissaoDefesaId] = useState('')
  const [previewDefesa, setPreviewDefesa] = useState(null)
  const [previewDefesaLoading, setPreviewDefesaLoading] = useState(false)
  const defesaInputRef = useRef(null)
  const previewDefesaDebounceRef = useRef(null)
  const defesaUsaPercentual = leilao?.defesa_modo === 'percentual'
  // Mesma regra de "faturar por parcela" usada em "Definir Venda" (ver
  // isPorParcelaLeilao/calcularQtdParcelas abaixo) — na defesa o valor digitado
  // é "por parcela" e multiplica pela quantidade de parcelas do meio de
  // pagamento padrão do leilão, sem multiplicar por quantidade de animais.
  const defesaPorParcela = leilao?.regra_faturamento === 'por_parcela'
  const calcularQtdParcelasLeilao = () => {
    if (!defesaPorParcela || !leilao?.meio_pagamento_padrao_id) return 1
    const meio = meiosPagamento.find(m => m.id === leilao.meio_pagamento_padrao_id)
    return meio?.grupos_parcelas?.reduce((acc, g) => acc + g.quantidade * (g.peso || 1), 0) || 1
  }

  // Meio de Pagamento (modais "Salvar como modelo" / "Meus Meios de Pagamento")
  const [showSalvarMeioPagamento, setShowSalvarMeioPagamento] = useState(false)
  const [nomeMeioPagamento, setNomeMeioPagamento] = useState('')
  const [showMeiosPagamentoModal, setShowMeiosPagamentoModal] = useState(false)
  const [showNovaComissaoModal, setShowNovaComissaoModal] = useState(false)
  // Índice da parcela (na lista de "Parcelas") com o campo de vencimento no
  // modo "data" em vez de "dias desde a anterior" — só de UI, não é salvo.
  const [parcelasEmModoData, setParcelasEmModoData] = useState({})
  // Parcelamento opcional da comissão de compra (fluxo cavalo)
  const [parcelarComissao, setParcelarComissao] = useState(false)
  const [comissaoParcelamento, setComissaoParcelamento] = useState(null)
  const [showParcelamentoComissao, setShowParcelamentoComissao] = useState(false)

  // Leiloeiro do lote (dentre os vinculados ao leilão)
  const [leileirosDoLeilaoList, setLeileirosDoLeilaoList] = useState([])

  // Assessor do lote (cadastro novo, independente do módulo Promotor/Assessoria) —
  // vínculo direto no lote, com % configurável neste lote específico.
  const [assessoresList, setAssessoresList] = useState([])

  // Quem Vendeu — trava o campo em "REMATEI" quando o lote veio do módulo
  // Pisteiro remoto sem o módulo base "quem_vendeu_trouxe" ativo (mesmo padrão do gado).
  const [quemVendeuTravado, setQuemVendeuTravado] = useState(false)

  // Preview de comissão — dados completos do PRIMEIRO vendedor do lote (o cavalo
  // permite múltiplos vendedores com %, mas a prévia de comissão/sócio aqui é uma
  // simplificação baseada só no primeiro; o rateio real por vendedor já acontece
  // corretamente no backend em gestao/routes.py, independente do que a prévia mostra).
  const [vendedorSelecionado, setVendedorSelecionado] = useState(null)
  const [comissaoCalculada, setComissaoCalculada] = useState(null)
  const [comissaoCustom, setComissaoCustom] = useState({ compra: null, venda: null })
  const [comissaoAlteradaManualmenteFlag, setComissaoAlteradaManualmenteFlag] = useState(false)
  const [ignorarIndividualVenda, setIgnorarIndividualVenda] = useState(false)
  const [ignorarIndividualCompra, setIgnorarIndividualCompra] = useState(false)
  const [confirmacaoTroca, setConfirmacaoTroca] = useState(null)

  useEffect(() => {
    if (organizadorId) {
      lotesService.listarRacas(organizadorId, { apenasAtivos: !isEditMode }).then(setRacas).catch(() => {})
      lotesService.listarTiposAnimais(organizadorId, { apenasAtivos: !isEditMode }).then(setTiposAnimal).catch(() => {})
      comissoesService.listar(organizadorId, { ativo: 'true' }).then(r => setComissoes(r.data || r)).catch(() => {})
      meiosPagamentoService.listar(organizadorId).then(r => setMeiosPagamento(r.data || r)).catch(() => {})
      organizadoresService.getConfiguracaoFaturamento(organizadorId).then(setConfiguracoesFaturamento).catch(() => {})
      assessoresService.listar(organizadorId, { ativo: true }).then(r => setAssessoresList(r.data || r)).catch(() => {})
    }
  }, [organizadorId])

  const handleCriarRaca = async (nome) => {
    try {
      const nova = await lotesService.criarRaca(organizadorId, { nome })
      setRacas(prev => [...prev, nova])
      return nova
    } catch (err) {
      console.error('Erro ao criar raça:', err)
      error('Erro ao criar raça')
      return null
    }
  }

  const handleCriarTipo = async (nome) => {
    try {
      const novo = await lotesService.criarTipoAnimal(organizadorId, { nome })
      setTiposAnimal(prev => [...prev, novo])
      return novo
    } catch (err) {
      console.error('Erro ao criar tipo de animal:', err)
      error('Erro ao criar tipo de animal')
      return null
    }
  }

  useEffect(() => {
    const idLeilao = lote?.leilao_id || leilaoIdParam
    if (organizadorId && idLeilao) {
      leiloesService.obterLeilao(organizadorId, idLeilao).then(l => {
        setLeilao(l)
        if (!isEditMode && l?.comissao_padrao_id) {
          setDadosFinanceiros(prev => ({ ...prev, comissao_id: String(l.comissao_padrao_id) }))
        }
      }).catch(() => {})
    }
  }, [organizadorId, leilaoIdParam, lote])

  // Carrega leiloeiros vinculados ao leilão para o dropdown "Leiloeiro" do financeiro
  // e auto-preenche com o primeiro da lista se ainda não houver escolha.
  useEffect(() => {
    const idLeilao = lote?.leilao_id || leilaoIdParam
    if (!organizadorId || !idLeilao) return
    leiloeiroService.listarDoLeilao(organizadorId, idLeilao)
      .then(listaLeiloeiros => {
        setLeileirosDoLeilaoList(listaLeiloeiros)
        if (listaLeiloeiros.length > 0 && !dadosFinanceiros.leiloeiro_id) {
          setDadosFinanceiros(prev => ({ ...prev, leiloeiro_id: listaLeiloeiros[0].leiloeiro_id }))
        }
      })
      .catch(() => {})
  }, [organizadorId, lote, leilaoIdParam])

  // Guarda com "aindaValido": em dev o React.StrictMode dispara este efeito duas
  // vezes no mount (monta → limpa → monta de novo). Sem essa guarda, a primeira
  // chamada de carregarLote() (já obsoleta) pode resolver DEPOIS da segunda e
  // sobrescrever dados mais novos (ex.: o valor de venda pré-preenchido pelo atalho
  // "Definir Valor de Venda" da lista de lotes) com o estado antigo do banco.
  useEffect(() => {
    if (!(isEditMode && organizadorId && loteId)) return
    let ativo = true
    carregarLote(() => ativo)
    return () => { ativo = false }
  }, [isEditMode, organizadorId, loteId])

  // Atalho "Definir Valor de Venda" (LotesLista) — mesmo padrão do lote de gado:
  // chega via navigate(..., { state: { step: 4, valorVendaUnidade, valorTotalPrecalculado, excecaoPorCabeca } })
  // e deve abrir direto no step Financeiro já com o valor preenchido.
  const valorVendaStateApplied = useRef(false)
  useEffect(() => {
    if (loading || !isEditMode) return
    if (location.state?.step !== 4) return
    if (valorVendaStateApplied.current) return
    valorVendaStateApplied.current = true

    const valor = location.state?.valorVendaUnidade
    const valorTotalPrecalculado = location.state?.valorTotalPrecalculado ?? null
    const excecaoPorCabeca = location.state?.excecaoPorCabeca || false

    if (excecaoPorCabeca) {
      setDadosFinanceiros(prev => ({ ...prev, modalidade_pagamento: 'A_VISTA', dias_prazo: '' }))
    }

    if (valor) {
      const qtd = animais.length || 1
      const valorTotal = valorTotalPrecalculado ?? (parseFloat(valor) * qtd)
      setDadosFinanceiros(prev => ({ ...prev, valor_total: valorTotal }))
      setValorVendaDefinido(true)
    }

    setCurrentStep(steps.length - 1)
    setTimeout(() => inputRefs.current['comprador']?.focus(), 300)
    window.history.replaceState({}, '', window.location.pathname)
  }, [loading])

  const carregarLote = async (aindaValido = () => true) => {
    try {
      setLoading(true)
      const [loteData, animaisData, vendedoresData, compradoresData] = await Promise.all([
        lotesService.obterLote(organizadorId, loteId),
        lotesService.listarAnimais(organizadorId, loteId),
        lotesService.listarVendedoresLote(organizadorId, loteId).catch(() => []),
        lotesService.listarCompradoresLote(organizadorId, loteId).catch(() => []),
      ])
      if (!aindaValido()) return
      setLote(loteData)
      setFormData({
        numero_lote: loteData.numero_lote || '',
        curral: loteData.curral || '',
        valor_minimo_unidade: numeroParaMoeda(loteData.valor_minimo_unidade),
        observacoes: loteData.observacoes || '',
        vendedor_id: loteData.vendedor_id || '',
        fazenda_id: loteData.fazenda_id || '',
        fazenda_ficticia: loteData.fazenda_ficticia || '',
      })
      setAnimais(animaisData.length ? animaisData.map(a => ({
        ...a,
        data_nascimento: a.data_nascimento ? a.data_nascimento.slice(0, 10) : '',
        raca_id: a.raca_id || '',
        percentual_venda: a.percentual_venda != null ? a.percentual_venda : 100,
        foto_url: a.foto_url || '',
        video_url: a.video_url || '',
        video_poster_url: a.video_poster_url || '',
        link_video: a.link_video || '',
      })) : [])
      setUsarFazendaFicticia(!!loteData.fazenda_ficticia)
      if (vendedoresData.length > 0) {
        setVendedoresLote(vendedoresData.map(v => ({
          vendedor_id: v.vendedor_id, vendedor_nome: v.vendedor_nome, percentual: v.percentual,
        })))
      } else if (loteData.vendedor) {
        setVendedoresLote([{ vendedor_id: loteData.vendedor_id, vendedor_nome: loteData.vendedor.nome, percentual: 100 }])
      }
      // Dados financeiros (comprador, comissão, parcelas, leiloeiro...) só são
      // recarregados do backend quando o lote está FATURADO — mesmo padrão do
      // lote de gado (NovoLote.jsx: só popula dadosFinanceiros dentro do
      // `if (lote.status === 'FATURADO' ...)`). Fora disso (NOVO/EDITADO, inclusive
      // logo após Desfazer Negociação, que limpa esses campos no banco), o estado
      // em memória permanece como estava — não apaga o que o usuário preencheu.
      // A comissão congelada (comissao_*_custom) pertence a um faturamento —
      // se o lote NÃO está mais faturado (ex: logo após Desfazer Negociação),
      // ela deixou de valer: zera pra recalcular a hierarquia ao vivo e não
      // travar o "✏️ Manual" numa % que não existe mais (bug: trocar de
      // comissão não pegava a nova, incl. uma comissão 0%).
      if (loteData.status !== 'FATURADO' && loteData.status !== 'DEFESA') {
        setComissaoCustom({ compra: null, venda: null })
        setComissaoAlteradaManualmenteFlag(false)
        setParcelarComissao(false)
        setComissaoParcelamento(null)
      }
      if (loteData.status === 'FATURADO' || loteData.status === 'DEFESA') {
        if (loteData.comprador) {
          setCompradorSelecionado(loteData.comprador)
          setSearchComprador(loteData.comprador.nome)
          carregarFazendasComprador(loteData.comprador_id, loteData.fazenda_comprador_id)
        }
        // Parcelamento da comissão de compra (fluxo cavalo)
        if (loteData.comissao_parcelamento) {
          setParcelarComissao(true)
          setComissaoParcelamento(loteData.comissao_parcelamento)
        } else {
          setParcelarComissao(false)
          setComissaoParcelamento(null)
        }
        // Rateio multi-comprador salvo (fluxo cavalo) — hidrata o editor
        if (Array.isArray(compradoresData) && compradoresData.length > 1) {
          setCompradoresLote(compradoresData.map(c => ({
            comprador_id: c.comprador_id,
            comprador_nome: c.comprador_nome,
            fazenda_comprador_id: c.fazenda_comprador_id ? String(c.fazenda_comprador_id) : '',
            percentual: c.percentual,
          })))
        }
        setComissaoCustom({
          compra: loteData.comissao_compra_custom != null ? Number(loteData.comissao_compra_custom) : null,
          venda: loteData.comissao_venda_custom != null ? Number(loteData.comissao_venda_custom) : null,
        })
        setComissaoAlteradaManualmenteFlag(!!loteData.comissao_alterada_manualmente)
        setValorVendaDefinido(true)
        setDadosFinanceiros(prev => ({
          ...prev,
          valor_total: loteData.valor_venda_total || '',
          fazenda_comprador_id: loteData.fazenda_comprador_id ? String(loteData.fazenda_comprador_id) : prev.fazenda_comprador_id,
          comissao_id: loteData.comissao_id ? String(loteData.comissao_id) : prev.comissao_id,
          desconto_tipo: loteData.desconto_valor ? 'valor' : (loteData.desconto_percentual ? 'percentual' : ''),
          desconto_percentual: loteData.desconto_percentual || '',
          desconto_valor: loteData.desconto_valor || '',
          modalidade_pagamento: loteData.modalidade_pagamento || 'A_VISTA',
          primeira_parcela_tipo: loteData.dados_pagamento?.primeira_parcela_tipo || prev.primeira_parcela_tipo,
          primeira_parcela_dias: loteData.dados_pagamento?.primeira_parcela_dias || prev.primeira_parcela_dias,
          grupos_parcelas: loteData.dados_pagamento?.grupos_parcelas?.length
            ? flattenGrupos(loteData.dados_pagamento.grupos_parcelas.map(g => ({ ...g, dias: g.dias ?? loteData.dados_pagamento?.intervalo_dias ?? 30 })))
            : prev.grupos_parcelas,
          quem_vendeu_id: loteData.quem_vendeu_id != null ? String(loteData.quem_vendeu_id) : prev.quem_vendeu_id,
          quem_vendeu_percentual: loteData.quem_vendeu_percentual != null ? loteData.quem_vendeu_percentual : prev.quem_vendeu_percentual,
          leiloeiro_id: loteData.leiloeiro_id != null ? loteData.leiloeiro_id : prev.leiloeiro_id,
          fora_do_leilao: loteData.fora_do_leilao || false,
          assessor_id: loteData.assessor_id != null ? loteData.assessor_id : prev.assessor_id,
          assessor_percentual: loteData.assessor_percentual != null ? loteData.assessor_percentual : prev.assessor_percentual,
        }))
      }
      // Lote marcado pelo módulo Pisteiro remoto sem o módulo base ativo — trava
      // "Quem Vendeu" em texto fixo (mesmo padrão do lote de gado).
      if (loteData.vendido_via_pisteiro_remoto && loteData.quem_vendeu_id) {
        setQuemVendeuTravado(true)
      }
    } catch (err) {
      if (!aindaValido()) return
      console.error('Erro ao carregar lote:', err)
      error('Erro ao carregar lote')
    } finally {
      if (aindaValido()) setLoading(false)
    }
  }

  // Marca "alterações não salvas" — chamado explicitamente nos onChange dos
  // campos de Lote/Animais/Vendedor (não via useEffect: um efeito observando os
  // estados inteiros dispara falsos positivos com atualizações automáticas, como
  // o auto-select de fazenda/propriedade logo após carregar o lote).
  const marcarAlterado = () => { if (isEditMode) setDadosAlterados(true) }

  // Junta as propriedades de TODOS os vendedores do lote — a propriedade não é
  // necessariamente só do vendedor principal, pode ser de qualquer um dos donos.
  const carregarFazendas = async (clienteIds) => {
    try {
      const listas = await Promise.all(
        clienteIds.map(id => clientesService.getById(organizadorId, id).catch(() => null))
      )
      const combinadas = listas
        .filter(Boolean)
        .flatMap(cliente => (cliente.fazendas || []).map(f => ({ ...f, _donoNome: cliente.nome })))
      setFazendas(combinadas)
      // Pré-seleciona a primeira propriedade (haras/fazenda) encontrada — só se
      // ainda não houver escolha manual nem "digitar manualmente" marcado.
      setFormData(prev => {
        if (prev.fazenda_id || usarFazendaFicticia || combinadas.length === 0) return prev
        return { ...prev, fazenda_id: String(combinadas[0].id) }
      })
    } catch (e) {
      console.error('Erro ao carregar fazendas:', e)
    }
  }

  // Recarrega as propriedades/fazendas sempre que a lista de vendedores mudar
  // (não mexe em formData.fazenda_id — carregarLote já traz o valor salvo do lote)
  const idsVendedoresKey = vendedoresLote.map(v => v.vendedor_id).filter(Boolean).join(',')
  useEffect(() => {
    const ids = vendedoresLote.map(v => v.vendedor_id).filter(Boolean)
    if (ids.length > 0 && organizadorId) {
      carregarFazendas(ids)
    }
  }, [idsVendedoresKey, organizadorId])

  const buscarComprador = (termo) => {
    clearTimeout(compradorSearchRef.current)
    setSearchComprador(termo)
    if (!termo || termo.length < 2) { setOpcoesComprador([]); return }
    compradorSearchRef.current = setTimeout(async () => {
      try {
        const data = await clientesService.getByOrganizador(organizadorId, 1, 20, termo)
        setOpcoesComprador(Array.isArray(data) ? data : (data?.data || []))
      } catch (e) { console.error(e) }
    }, 300)
  }

  const carregarFazendasComprador = async (clienteId, manterFazendaId = null) => {
    if (!clienteId) { setFazendasComprador([]); return }
    try {
      const cliente = await clientesService.getById(organizadorId, clienteId)
      const fazendasCliente = cliente.fazendas || []
      setFazendasComprador(fazendasCliente)
      // Se veio um ID pra manter (ex: reabrindo lote já faturado), preserva a fazenda
      // já salva em vez de sobrescrever com a "padrão" (última cadastrada) do cliente.
      if (manterFazendaId) {
        setDadosFinanceiros(prev => ({ ...prev, fazenda_comprador_id: manterFazendaId.toString() }))
      } else if (fazendasCliente.length > 0) {
        const fazendaPadrao = fazendasCliente[fazendasCliente.length - 1]
        setDadosFinanceiros(prev => ({ ...prev, fazenda_comprador_id: fazendaPadrao.id.toString() }))
      } else {
        setDadosFinanceiros(prev => ({ ...prev, fazenda_comprador_id: '' }))
      }
    } catch (e) {
      console.error('Erro ao carregar fazendas do comprador:', e)
      setFazendasComprador([])
    }
  }

  const selecionarComprador = (cliente) => {
    if (cliente.restrito_compra) {
      setCompradorRestritoModal(cliente)
      setOpcoesComprador([])
      setSearchComprador('')
      return
    }
    setCompradorSelecionado(cliente)
    setSearchComprador(cliente.nome)
    setOpcoesComprador([])
    carregarFazendasComprador(cliente.id)

    // Pré-seleciona o meio de pagamento padrão configurado no leilão — só se o
    // usuário ainda não tiver mexido na modalidade (continua no default A_VISTA).
    if (leilao?.meio_pagamento_padrao_id && dadosFinanceiros.modalidade_pagamento === 'A_VISTA') {
      const meioPadrao = meiosPagamento.find(m => m.id === leilao.meio_pagamento_padrao_id)
      if (meioPadrao) {
        setDadosFinanceiros(prev => ({
          ...prev,
          modalidade_pagamento: meioPadrao.tipo,
          dias_prazo: meioPadrao.dias_prazo || '',
          primeira_parcela_tipo: meioPadrao.primeira_parcela_tipo || 'A_VISTA',
          primeira_parcela_dias: meioPadrao.primeira_parcela_dias || '',
          grupos_parcelas: flattenGrupos((meioPadrao.grupos_parcelas || prev.grupos_parcelas)
            .map(g => ({ ...g, dias: g.dias ?? meioPadrao.intervalo_dias ?? 30 }))),
        }))
      }
    }
  }

  const confirmarCompradorRestrito = () => {
    const cliente = compradorRestritoModal
    if (!cliente) return
    setCompradorSelecionado(cliente)
    setSearchComprador(cliente.nome)
    carregarFazendasComprador(cliente.id)
    setCompradorRestritoModal(null)
  }

  // Carrega o cliente completo do PRIMEIRO vendedor do lote — usado só para a prévia
  // de comissão/sócio (ver comentário do estado vendedorSelecionado acima).
  const idPrimeiroVendedor = vendedoresLote[0]?.vendedor_id
  useEffect(() => {
    if (idPrimeiroVendedor && organizadorId) {
      clientesService.getById(organizadorId, idPrimeiroVendedor).then(setVendedorSelecionado).catch(() => setVendedorSelecionado(null))
    } else {
      setVendedorSelecionado(null)
    }
  }, [idPrimeiroVendedor, organizadorId])

  // A escolha de "ignorar individual" não deve vazar de um cliente pro outro quando o
  // comprador/vendedor muda antes de faturar.
  useEffect(() => { setIgnorarIndividualVenda(false) }, [vendedorSelecionado?.id])
  useEffect(() => { setIgnorarIndividualCompra(false) }, [compradorSelecionado?.id])

  // Espelha o comprador principal (índice 0 do editor de co-compradores) a
  // partir do comprador escolhido acima e da fazenda selecionada. Preserva os
  // co-compradores já adicionados (índices > 0) e reajusta a % do principal.
  useEffect(() => {
    if (!compradorSelecionado) { setCompradoresLote([]); return }
    setCompradoresLote(prev => {
      const extras = prev.slice(1)
      const somaExtras = extras.reduce((s, c) => s + (parseFloat(c.percentual) || 0), 0)
      const principal = {
        comprador_id: compradorSelecionado.id,
        comprador_nome: compradorSelecionado.nome,
        fazenda_comprador_id: dadosFinanceiros.fazenda_comprador_id || '',
        percentual: extras.length ? Math.max(0, Math.round((100 - somaExtras) * 100) / 100) : 100,
      }
      return [principal, ...extras]
    })
  }, [compradorSelecionado?.id, compradorSelecionado?.nome, dadosFinanceiros.fazenda_comprador_id])

  // onChange do editor: mantém a fazenda do principal em sincronia com o
  // dropdown de fazenda do formulário (fonte única).
  const handleCompradoresChange = (novos) => {
    setCompradoresLote(novos)
    const fazPrincipal = novos[0]?.fazenda_comprador_id || ''
    if (fazPrincipal !== (dadosFinanceiros.fazenda_comprador_id || '')) {
      setDadosFinanceiros(prev => ({ ...prev, fazenda_comprador_id: fazPrincipal }))
    }
  }

  // Lista de co-compradores válida para enviar no faturamento (2+ com cliente
  // preenchido e soma = 100). Vazia = faturamento de comprador único (inércia).
  const compradoresPayload = (() => {
    const validos = compradoresLote.filter(c => c.comprador_id)
    if (validos.length < 2) return null
    const soma = validos.reduce((s, c) => s + (parseFloat(c.percentual) || 0), 0)
    if (Math.abs(soma - 100) > 0.01) return null
    return validos.map((c, i) => ({
      comprador_id: parseInt(c.comprador_id),
      fazenda_comprador_id: c.fazenda_comprador_id ? parseInt(c.fazenda_comprador_id) : null,
      percentual: parseFloat(c.percentual) || 0,
      ordem: i,
    }))
  })()
  const compradoresIncompletos = compradoresLote.length > 1 && !compradoresPayload

  // Salva o rateio de compradores num lote JÁ FATURADO (endpoint PUT
  // .../compradores aceita FATURADO e re-sincroniza a Gestão sozinho).
  const handleSalvarRateio = async () => {
    const validos = compradoresLote.filter(c => c.comprador_id)
    if (validos.length === 0) { error('Selecione ao menos um comprador'); return }
    let payload
    if (validos.length === 1) {
      payload = [{
        comprador_id: parseInt(validos[0].comprador_id),
        fazenda_comprador_id: validos[0].fazenda_comprador_id ? parseInt(validos[0].fazenda_comprador_id) : null,
        percentual: 100, ordem: 0,
      }]
    } else {
      if (!compradoresPayload) { error('Preencha os compradores e feche a soma em 100%'); return }
      payload = compradoresPayload
    }
    try {
      setSalvandoRateio(true)
      await lotesService.salvarCompradoresLote(organizadorId, loteId, payload)
      success('Rateio de compradores atualizado')
      setEditandoRateio(false)
      carregarLote()
    } catch (err) {
      console.error('Erro ao salvar rateio de compradores:', err)
      error(err.response?.data?.detail || 'Erro ao salvar rateio de compradores')
    } finally {
      setSalvandoRateio(false)
    }
  }

  // Porte de NovoLote.jsx (calcularComissaoAutomatica) — decide o percentual de
  // comissão de compra/venda considerando: individual do cliente > cenário de sócios >
  // padrão da tabela. Mesma lógica usada pelo backend em gestao/routes.py.
  const calcularComissaoAutomatica = (vendedorId, compradorId, comissaoId) => {
    if (!vendedorId || !compradorId || !comissaoId) return null

    const vendedor = vendedorSelecionado?.id?.toString() === vendedorId ? vendedorSelecionado : null
    const comprador = compradorSelecionado?.id?.toString() === compradorId ? compradorSelecionado : null
    const comissao = comissoes?.find(c => c.id.toString() === comissaoId)

    if (!vendedor || !comprador || !comissao) return comissao

    let comissaoCalc = { ...comissao }

    const ccIndividual = comprador.comissao_compra_pct != null ? Number(comprador.comissao_compra_pct) : null
    const cvIndividual = vendedor.comissao_venda_pct != null ? Number(vendedor.comissao_venda_pct) : null

    let cc = ignorarIndividualCompra ? null : ccIndividual
    let cv = ignorarIndividualVenda ? null : cvIndividual

    const usaSocios = organizadorAtivo?.usa_comissao_socios
    let ccSocio = null, cvSocio = null, cdSocio = null, cenarioSocio = null
    if (usaSocios && comissao.tipo_comissao === 'socios') {
      const vendedorSocio = vendedor.is_socio
      const compradorSocio = comprador.is_socio

      if (vendedorSocio && compradorSocio) {
        ccSocio = comissao.ambos_socios_comprador
        cvSocio = comissao.ambos_socios_vendedor
        cdSocio = comissao.ambos_socios_defesa
        cenarioSocio = 'Ambos Sócios'
      } else if (!vendedorSocio && compradorSocio) {
        ccSocio = comissao.comprador_socio_comprador
        cvSocio = comissao.comprador_socio_vendedor
        cdSocio = comissao.comprador_socio_defesa
        cenarioSocio = 'Só Comprador Sócio'
      } else if (vendedorSocio && !compradorSocio) {
        ccSocio = comissao.vendedor_socio_comprador
        cvSocio = comissao.vendedor_socio_vendedor
        cdSocio = comissao.vendedor_socio_defesa
        cenarioSocio = 'Só Vendedor Sócio'
      } else {
        cenarioSocio = 'Nenhum Sócio'
      }
    }
    if (cc == null && ccSocio != null) cc = ccSocio
    if (cv == null && cvSocio != null) cv = cvSocio

    if (ccIndividual != null) comissaoCalc.cenario_compra_individual = true
    if (cvIndividual != null) comissaoCalc.cenario_venda_individual = true
    comissaoCalc.alternativa_compra_pct = ccSocio ?? comissao.comissao_compra
    comissaoCalc.alternativa_venda_pct = cvSocio ?? comissao.comissao_venda
    comissaoCalc.alternativa_compra_label =
      cenarioSocio === 'Ambos Sócios' ? 'Ambos sócios' :
      cenarioSocio === 'Só Comprador Sócio' ? 'Sócio' :
      cenarioSocio ? 'Não Sócio' : 'Padrão'
    comissaoCalc.alternativa_venda_label =
      cenarioSocio === 'Ambos Sócios' ? 'Ambos sócios' :
      cenarioSocio === 'Só Vendedor Sócio' ? 'Sócio' :
      cenarioSocio ? 'Não Sócio' : 'Padrão'
    comissaoCalc.alternativa_compra_detalhe = cenarioSocio === 'Só Vendedor Sócio'
      ? 'Vendedor é sócio (comprador não é)'
      : ccSocio != null ? `Sócio (${cenarioSocio})` : 'Padrão da tabela'
    comissaoCalc.alternativa_venda_detalhe = cenarioSocio === 'Só Comprador Sócio'
      ? 'Comprador é sócio (vendedor não é)'
      : cvSocio != null ? `Sócio (${cenarioSocio})` : 'Padrão da tabela'

    comissaoCalc.comissao_compra = cc ?? comissao.comissao_compra
    comissaoCalc.comissao_venda = cv ?? comissao.comissao_venda
    comissaoCalc.comissao_defesa = cdSocio ?? comissao.comissao_defesa
    comissaoCalc.cenario = cenarioSocio

    return comissaoCalc
  }

  // Um percentual congelado só é realmente "alterado" se divergir do que a hierarquia
  // calcularia hoje — ver comentário completo na versão original em NovoLote.jsx.
  const comissaoDivergente = (customVal, calculadaVal) => {
    if (customVal == null) return false
    if (calculadaVal == null) return true
    return Math.abs(Number(customVal) - Number(calculadaVal)) > 0.005
  }

  const origemDivergencia = (customVal, calculadaVal, individualVal) => {
    if (!comissaoDivergente(customVal, calculadaVal)) return null
    if (comissaoAlteradaManualmenteFlag) return 'manual'
    return individualVal != null ? 'cadastro' : 'manual'
  }

  const LIMIAR_CONFIRMACAO_TROCA = 0.20

  const escolherComissaoLado = (lado, pctAtual, pctDestino, labelDestino, destino, setIgnorando) => {
    const diffRelativa = pctAtual > 0 ? Math.abs(pctAtual - pctDestino) / pctAtual : (pctDestino > 0 ? 1 : 0)
    if (diffRelativa > LIMIAR_CONFIRMACAO_TROCA) {
      setConfirmacaoTroca({ lado, pctAtual, pctNovo: pctDestino, label: labelDestino, destino, setIgnorando })
    } else {
      setIgnorando(destino)
    }
  }

  const CORES_CARTAO_COMISSAO = {
    yellow: { border: 'border-yellow-400', bg: 'bg-yellow-50', text: 'text-yellow-800' },
    amber: { border: 'border-amber-500', bg: 'bg-amber-50', text: 'text-amber-700' },
    blue: { border: 'border-blue-300', bg: 'bg-blue-50', text: 'text-blue-700' },
  }

  const renderCartaoComissao = (card) => {
    const cor = CORES_CARTAO_COMISSAO[card.cor]
    const selecionado = card.selecionado
    return (
      <button
        key={card.key}
        type="button"
        disabled={!card.onClick}
        onClick={card.onClick || undefined}
        className={`min-h-[72px] p-2 rounded border flex flex-col items-center justify-center text-center transition-colors ${
          selecionado ? `${cor.border} ${cor.bg}` : 'border-gray-200 bg-white hover:bg-gray-50'
        } ${card.onClick ? '' : 'cursor-default'}`}
      >
        <div className={`text-[10px] font-semibold ${selecionado ? cor.text : 'text-gray-400'}`}>{card.grupo}</div>
        <div className={`text-[10px] ${selecionado ? cor.text : 'text-gray-400'}`}>{card.tipo}</div>
        <div className={`text-base font-bold ${selecionado ? cor.text : 'text-gray-500'}`}>{card.pct.toFixed(2)}%</div>
      </button>
    )
  }

  const getCartoesLado = (lado) => {
    if (!comissaoCalculada) return []
    const isCompra = lado === 'compra'
    const grupo = isCompra ? 'Comprador' : 'Vendedor'
    const cliente = isCompra ? compradorSelecionado : vendedorSelecionado
    const individualPctRaw = cliente ? (isCompra ? cliente.comissao_compra_pct : cliente.comissao_venda_pct) : null
    const individualExiste = individualPctRaw != null
    const individualPct = individualExiste ? Number(individualPctRaw) : null
    const alternativaPct = Number(isCompra ? comissaoCalculada.alternativa_compra_pct : comissaoCalculada.alternativa_venda_pct)
    const alternativaLabel = isCompra ? comissaoCalculada.alternativa_compra_label : comissaoCalculada.alternativa_venda_label
    const alternativaDetalhe = isCompra ? comissaoCalculada.alternativa_compra_detalhe : comissaoCalculada.alternativa_venda_detalhe
    const ignorando = isCompra ? ignorarIndividualCompra : ignorarIndividualVenda
    const setIgnorando = isCompra ? setIgnorarIndividualCompra : setIgnorarIndividualVenda
    const custom = isCompra ? comissaoCustom.compra : comissaoCustom.venda
    const calculadaPct = isCompra ? comissaoCalculada.comissao_compra : comissaoCalculada.comissao_venda

    if (custom != null) {
      const origem = origemDivergencia(custom, calculadaPct, individualPctRaw)
      const bateComIndividual = individualExiste && Math.abs(custom - individualPct) < 0.005
      const tipo = origem === 'manual' ? '✏️ Manual' : bateComIndividual ? '🔒 Individual' : '🔒 Congelada'
      return [{
        key: `${lado}-custom`, grupo, tipo,
        pct: custom, selecionado: true, cor: bateComIndividual ? 'yellow' : 'amber', onClick: null,
      }]
    }

    if (!individualExiste) {
      return [{
        key: `${lado}-unico`, grupo, tipo: alternativaLabel,
        pct: Number(calculadaPct), selecionado: true, cor: 'blue', onClick: null,
      }]
    }

    return [
      {
        key: `${lado}-individual`, grupo, tipo: 'Individual',
        pct: individualPct, selecionado: !ignorando, cor: 'yellow',
        onClick: !ignorando ? null : () => escolherComissaoLado(lado, alternativaPct, individualPct, 'Individual', false, setIgnorando),
      },
      {
        key: `${lado}-alternativa`, grupo, tipo: alternativaLabel,
        pct: alternativaPct, selecionado: ignorando, cor: 'amber',
        onClick: ignorando ? null : () => escolherComissaoLado(lado, individualPct, alternativaPct, alternativaDetalhe, true, setIgnorando),
      },
    ]
  }

  const valorLadoComissao = (lado) => {
    if (!comissaoCalculada) return 0
    const isCompra = lado === 'compra'
    const custom = isCompra ? comissaoCustom.compra : comissaoCustom.venda
    const calculadaPct = isCompra ? comissaoCalculada.comissao_compra : comissaoCalculada.comissao_venda
    const pctExibido = custom != null ? custom : calculadaPct
    return ((dadosFinanceiros.valor_total || 0) * pctExibido) / 100
  }

  useEffect(() => {
    if (compradorSelecionado?.id && idPrimeiroVendedor && dadosFinanceiros.comissao_id && comissoes.length) {
      const calc = calcularComissaoAutomatica(String(idPrimeiroVendedor), String(compradorSelecionado.id), dadosFinanceiros.comissao_id)
      setComissaoCalculada(calc)
    } else {
      setComissaoCalculada(null)
    }
  }, [compradorSelecionado?.id, idPrimeiroVendedor, dadosFinanceiros.comissao_id, vendedorSelecionado, comissoes, ignorarIndividualVenda, ignorarIndividualCompra])

  // Auto-seleciona a comissão quando ainda não há escolha: prioriza a comissão
  // padrão do leilão, depois a marcada como is_padrao, depois — se só existe uma
  // opção cadastrada — essa única (comum no fluxo cavalo, com uma comissão só).
  useEffect(() => {
    if (dadosFinanceiros.comissao_id || comissoes.length === 0) return
    const padraoLeilao = leilao?.comissao_padrao_id
      ? comissoes.find(c => c.id === leilao.comissao_padrao_id)
      : null
    const padraoFlag = comissoes.find(c => c.is_padrao)
    const unica = comissoes.length === 1 ? comissoes[0] : null
    const escolhida = padraoLeilao || padraoFlag || unica
    if (escolhida) {
      setDadosFinanceiros(prev => ({ ...prev, comissao_id: String(escolhida.id) }))
    }
  }, [comissoes, leilao, dadosFinanceiros.comissao_id])

  // Mesma regra do modal "Definir Valor de Venda" da lista de lotes (LotesLista.jsx):
  // se o leilão fatura por parcela (peso), o valor digitado é "por parcela" e o total
  // multiplica pela quantidade de unidades de peso do meio de pagamento padrão — a
  // menos que o usuário marque a exceção "faturar por cabeça" pra esse lote.
  const isPorParcelaLeilao = leilao?.regra_faturamento === 'por_parcela' && !valorVendaExcecaoPorCabeca
  const calcularQtdParcelas = () => {
    if (!isPorParcelaLeilao || !leilao?.meio_pagamento_padrao_id) return 1
    const meio = meiosPagamento.find(m => m.id === leilao.meio_pagamento_padrao_id)
    return meio?.grupos_parcelas?.reduce((acc, g) => acc + g.quantidade * (g.peso || 1), 0) || 1
  }

  const confirmarValorVenda = () => {
    if (!valorVendaInput || parseFloat(valorVendaInput) <= 0) return
    const qtd = animais.length || 1
    const valorUnit = parseFloat(valorVendaInput)
    const qtdParcelas = calcularQtdParcelas()
    const valorTotal = isPorParcelaLeilao ? valorUnit * qtdParcelas * qtd : valorUnit * qtd
    setDadosFinanceiros(prev => ({ ...prev, valor_total: valorTotal }))
    setValorVendaDefinido(true)
    setShowValorVendaModal(false)
    setValorVendaInput('')
    setValorVendaExcecaoPorCabeca(false)
    setTimeout(() => inputRefs.current['comprador']?.focus(), 100)
  }

  const isStepValid = (stepIndex) => {
    const stepId = steps[stepIndex]?.id
    switch (stepId) {
      case 'lote': return !!formData.numero_lote
      case 'animais': return animais.length > 0 && animais.every(a => a.nome?.trim() && a.sexo && a.tipo_animal_id)
      case 'vendedor': return vendedoresLote.length > 0 && vendedoresLote.every(v => v.vendedor_id)
      default: return true
    }
  }

  const nextStep = () => { if (currentStep < steps.length - 1) setCurrentStep(currentStep + 1) }
  const prevStep = () => { if (currentStep > 0) setCurrentStep(currentStep - 1) }

  // Navegação por Enter: igual ao wizard de gado — Enter foca o próximo campo do
  // mesmo step; só no último campo do step é que avança/salva/fatura.
  const inputRefs = useRef({})
  const setFieldRef = (name) => (el) => { inputRefs.current[name] = el }

  const runStepAdvance = () => {
    const stepIdAtual = steps[currentStep]?.id
    if (stepIdAtual === 'financeiro') {
      if (!loteFechado) handleFaturar()
    } else if (stepIdAtual === 'despesas') {
      // sem avanço automático por Enter nesse step (mesmo comportamento de sempre)
    } else if (currentStep === steps.length - 1) {
      if (isStepValid(currentStep)) handleSalvarLote()
    } else if (isStepValid(currentStep)) {
      nextStep()
    }
  }

  const handleFieldKeyDown = (e, nextField) => {
    if (e.key !== 'Enter') return
    e.preventDefault()
    if (nextField && inputRefs.current[nextField]) {
      inputRefs.current[nextField].focus()
      return
    }
    runStepAdvance()
  }

  // Campos de autocomplete (vendedor/comprador): Enter seleciona a 1ª sugestão da
  // lista se estiver aberta; senão, comporta-se como campo normal (foca o próximo).
  const handleAutocompleteKeyDown = (e, opcoes, onSelecionar, nextField) => {
    if (e.key !== 'Enter') return
    e.preventDefault()
    if (opcoes.length > 0) {
      onSelecionar(opcoes[0])
      return
    }
    if (nextField && inputRefs.current[nextField]) {
      inputRefs.current[nextField].focus()
      return
    }
    runStepAdvance()
  }

  const handleSalvarLote = async () => {
    const vendedoresValidos = vendedoresLote.every(v => v.vendedor_id)
    const animaisValidos = animais.every(a => a.nome?.trim() && a.sexo && a.tipo_animal_id)
    if (!formData.numero_lote || animais.length === 0 || !vendedoresValidos || !animaisValidos) {
      error('Preencha número do lote, o(s) vendedor(es) e, para cada animal, nome, sexo e tipo')
      return
    }
    try {
      setSaving(true)
      const payload = {
        numero_lote: formData.numero_lote,
        qtd: animais.length,
        curral: formData.curral || null,
        valor_minimo_unidade: moedaParaNumero(formData.valor_minimo_unidade),
        observacoes: formData.observacoes || null,
        leilao_id: parseInt(lote?.leilao_id || leilaoIdParam),
        vendedor_id: parseInt(vendedoresLote[0].vendedor_id),
        fazenda_id: usarFazendaFicticia ? null : (formData.fazenda_id ? parseInt(formData.fazenda_id) : null),
        fazenda_ficticia: usarFazendaFicticia ? formData.fazenda_ficticia : null,
      }

      let loteAtual
      if (isEditMode) {
        loteAtual = await lotesService.atualizarLote(organizadorId, loteId, payload)
      } else {
        loteAtual = await lotesService.criarLote(organizadorId, payload)
      }

      const animaisPayload = animais.map((a, i) => ({
        nome: a.nome,
        sexo: a.sexo || null,
        data_nascimento: a.data_nascimento || null,
        pelagem: a.pelagem || null,
        peso: a.peso ? parseFloat(a.peso) : null,
        percentual_venda: a.percentual_venda !== '' && a.percentual_venda != null ? parseFloat(a.percentual_venda) : 100,
        pai: a.pai || null,
        pai_registro: a.pai_registro || null,
        mae: a.mae || null,
        mae_registro: a.mae_registro || null,
        genealogia_avos: a.genealogia_avos && Object.keys(a.genealogia_avos).length ? a.genealogia_avos : null,
        localizacao: a.localizacao || null,
        raca_id: a.raca_id ? parseInt(a.raca_id) : null,
        tipo_animal_id: a.tipo_animal_id ? parseInt(a.tipo_animal_id) : null,
        registro: a.registro || null,
        informacoes: a.informacoes || null,
        ordem: i,
        foto_url: a.foto_url || null,
        video_url: a.video_url || null,
        video_poster_url: a.video_poster_url || null,
        link_video: a.link_video || null,
        animal_cadastro_id: a.animal_cadastro_id || null,
        ...(a.video_pending_key ? { video_pending_key: a.video_pending_key } : {}),
        ...(a.poster_pending_key ? { poster_pending_key: a.poster_pending_key } : {}),
        ...(a.foto_pending_key ? { foto_pending_key: a.foto_pending_key } : {}),
      }))
      await lotesService.salvarAnimais(organizadorId, loteAtual.id, animaisPayload)

      const vendedoresPayload = vendedoresLote.map((v, i) => ({
        vendedor_id: parseInt(v.vendedor_id),
        percentual: v.percentual !== '' && v.percentual != null ? parseFloat(v.percentual) : 100,
        ordem: i,
      }))
      await lotesService.salvarVendedoresLote(organizadorId, loteAtual.id, vendedoresPayload)

      if (!isEditMode) {
        setLoteSalvoModal({ numero: loteAtual.numero_lote, id: loteAtual.id })
      } else {
        success('Lote atualizado com sucesso!')
        setDadosAlterados(false)
        carregarLote()
      }
    } catch (err) {
      console.error('Erro ao salvar lote:', err)
      error(err.response?.data?.detail || 'Erro ao salvar lote')
    } finally {
      setSaving(false)
    }
  }

  // Limpa o formulário pra cadastrar outro lote sem sair da página (atalho do
  // popup pós-salvamento) — só faz sentido no modo de criação.
  const resetParaNovoLote = () => {
    setFormData({
      numero_lote: '',
      curral: '',
      valor_minimo_unidade: '',
      observacoes: '',
      vendedor_id: '',
      fazenda_id: '',
      fazenda_ficticia: '',
    })
    setAnimais([])
    setVendedoresLote([{ vendedor_id: '', vendedor_nome: '', percentual: 100 }])
    setUsarFazendaFicticia(false)
    setCurrentStep(0)
    setLoteSalvoModal(null)
    setTimeout(() => inputRefs.current['numero_lote']?.focus(), 100)
  }

  // Desfazer negociação (mesmo padrão do lote de gado): se já houver parcela paga
  // vinculada ao lote, avisa com um toast de confirmação antes de excluir os
  // lançamentos (em vez de window.confirm — mesmo padrão usado em PromotoresLeilao.jsx).
  const [desfazendo, setDesfazendo] = useState(false)

  // Dropdowns de Contratos/Notas no header (mesmo padrão visual do lote de gado)
  const [showContratosDropdown, setShowContratosDropdown] = useState(false)
  const [showNotasDropdown, setShowNotasDropdown] = useState(false)
  const contratosDropdownRef = useRef(null)
  const notasDropdownRef = useRef(null)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contratosDropdownRef.current && !contratosDropdownRef.current.contains(e.target)) {
        setShowContratosDropdown(false)
      }
      if (notasDropdownRef.current && !notasDropdownRef.current.contains(e.target)) {
        setShowNotasDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])
  const desfazerNegociacao = async () => {
    try {
      const res = await gestaoService.listarParcelasNegociacao(organizadorId, { lote_id: loteId })
      const parcelas = (res.data || res || []).filter(p => String(p.lote_id) === String(loteId))
      const pagas = parcelas.filter(p => p.status === 'pago')
      if (pagas.length > 0) {
        const totalPago = pagas.reduce((s, p) => s + Number(p.valor || 0), 0)
        const fmtValor = totalPago.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        toast((t) => (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-gray-800">
              Este lote possui <strong>{pagas.length} parcela{pagas.length > 1 ? 's' : ''}</strong> já paga{pagas.length > 1 ? 's' : ''} ({fmtValor}).
            </p>
            <p className="text-xs text-gray-500">Ao desfazer, esses lançamentos serão excluídos permanentemente.</p>
            <div className="flex gap-2 mt-1">
              <button onClick={() => { toast.dismiss(t.id); executarDesfazer() }}
                className="flex-1 px-3 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700">
                Desfazer mesmo assim
              </button>
              <button onClick={() => toast.dismiss(t.id)}
                className="flex-1 px-3 py-1.5 text-xs font-semibold border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50">
                Cancelar
              </button>
            </div>
          </div>
        ), { duration: 15000, position: 'top-center', style: { maxWidth: '380px' } })
        return
      }
    } catch {
      // se a verificação falhar, segue sem bloquear
    }
    await executarDesfazer()
  }

  const executarDesfazer = async () => {
    setDesfazendo(true)
    try {
      await faturamentoService.desfazerNegociacao(organizadorId, loteId)
      await carregarLote()
      success('Negociação desfeita com sucesso!')
    } catch (err) {
      console.error('Erro ao desfazer negociação:', err)
      error('Erro ao desfazer negociação')
    } finally {
      setDesfazendo(false)
    }
  }

  // Resetar (mesmo padrão do gado): volta o lote pra status NOVO, limpando por
  // completo os dados financeiros — diferente do "Desfazer Negociação" (que
  // preserva o que já foi preenchido pra facilitar refaturar).
  const resetarLote = async () => {
    if (quemVendeuTravado && !window.confirm(
      'Este lote foi vendido pelo Pisteiro Rematei. Resetar vai apagar o vínculo com o comprador e a comissão registrada, sem volta. Tem certeza que quer resetar?'
    )) {
      return
    }
    try {
      await faturamentoService.resetarLote(organizadorId, loteId)
      setValorVendaDefinido(false)
      setCompradorSelecionado(null)
      setSearchComprador('')
      setDadosFinanceiros(prev => ({
        ...prev,
        valor_total: '',
        comprador_id: '',
        fazenda_comprador_id: '',
        comissao_id: '',
        modalidade_pagamento: 'A_VISTA',
        desconto_tipo: '',
        desconto_percentual: '',
        desconto_valor: '',
      }))
      setQuemVendeuTravado(false)
      success('Lote resetado com sucesso!')
      await carregarLote()
    } catch (err) {
      console.error('Erro ao resetar lote:', err)
      error('Erro ao resetar lote')
    }
  }

  // Mesma regra do gado (calcularDataParcela em NovoLote.jsx): respeita a
  // configuração do organizador em Configurações > Faturamento — "Dias Corridos"
  // soma dias direto na data; "Dia Fixo" avança em meses cheios (mantém o dia).
  const calcularDataParcelaCavalo = (dataBase, dias) => {
    const data = new Date(dataBase)
    const tipoCalculo = configuracoesFaturamento?.tipo_calculo_parcelas || 'DIAS_CORRIDOS'
    if (tipoCalculo === 'DIAS_CORRIDOS') {
      data.setDate(data.getDate() + dias)
    } else {
      // DIA_FIXO_MENSAL — soma meses cheios mantendo o dia; se o mês de
      // destino não tiver esse dia (ex: dia 29/30/31 caindo em fevereiro),
      // usa o último dia válido do mês em vez de estourar pro mês seguinte
      // (comportamento padrão do Date.setMonth do JS).
      const mesesAdicionar = Math.floor(dias / 30)
      const diaOriginal = data.getDate()
      data.setDate(1) // evita overflow ao trocar de mês
      data.setMonth(data.getMonth() + mesesAdicionar)
      const ultimoDiaDoMes = new Date(data.getFullYear(), data.getMonth() + 1, 0).getDate()
      data.setDate(Math.min(diaOriginal, ultimoDiaDoMes))
    }
    return data
  }

  // Sempre trabalha com 1 parcela por "grupo" (quantidade:1) — cada linha na
  // tela é uma parcela de verdade. Grupos antigos com quantidade > 1
  // (templates salvos antes dessa mudança, ou atalhos como "12x iguais") são
  // "abertos" em N linhas individuais aqui.
  const flattenGrupos = (grupos) => {
    const flat = []
    ;(grupos || []).forEach(g => {
      const n = g.quantidade || 1
      for (let i = 0; i < n; i++) flat.push({ quantidade: 1, peso: g.peso || 1, dias: g.dias ?? 30 })
    })
    return flat.length > 0 ? flat : [{ quantidade: 1, peso: 1, dias: 30 }]
  }

  // Porte de gerarParcelasAutomaticas (NovoLote.jsx) adaptado ao cavalo: aqui NÃO
  // existe fluxo "separada"/"não-separada" nem desconto de comissão/despesas na 1ª
  // parcela — o vendedor paga comissão de venda + despesas + taxa de inscrição à
  // parte, via Gestão Financeira. Por isso sempre divide (valor_total - desconto)
  // proporcionalmente pelos grupos de parcela, sem descontar nada da 1ª parcela
  // (equivalente ao ramo isSeparada=true do gado).
  const gerarParcelasAutomaticas = () => {
    const vv = Number(dadosFinanceiros.valor_total) || 0
    if (!vv || !dadosFinanceiros.grupos_parcelas?.length) return []

    let desconto = 0
    if (dadosFinanceiros.desconto_tipo === 'percentual' && dadosFinanceiros.desconto_percentual) {
      desconto = vv * parseFloat(dadosFinanceiros.desconto_percentual) / 100
    } else if (dadosFinanceiros.desconto_tipo === 'valor' && dadosFinanceiros.desconto_valor) {
      desconto = parseFloat(dadosFinanceiros.desconto_valor)
    }
    const valorLiquido = vv - desconto

    const totalUnidadesPeso = dadosFinanceiros.grupos_parcelas.reduce((acc, g) => acc + (g.quantidade * g.peso), 0)
    if (totalUnidadesPeso <= 0) return []
    const valorPorUnidadePeso = valorLiquido / totalUnidadesPeso

    // Data base: só usa a data do leilão se o organizador configurou assim em
    // Configurações > Faturamento — senão usa hoje (mesma regra do gado).
    let dataBase = (configuracoesFaturamento?.data_base_calculo === 'DATA_LEILAO' && leilao?.data_leilao)
      ? new Date(leilao.data_leilao)
      : new Date()
    if (dadosFinanceiros.primeira_parcela_tipo === 'DIAS' && dadosFinanceiros.primeira_parcela_dias) {
      dataBase = calcularDataParcelaCavalo(dataBase, parseInt(dadosFinanceiros.primeira_parcela_dias) || 0)
    }

    let dataAtual = new Date(dataBase)
    const parcelas = []

    // 1 linha = 1 parcela. A 1ª parcela usa a data base (acima); cada parcela
    // seguinte usa o "dias" da sua PRÓPRIA linha, contado a partir da
    // parcela anterior — por isso a 1ª linha não tem campo de dias.
    dadosFinanceiros.grupos_parcelas.forEach((grupo, index) => {
      if (index > 0) {
        const intervaloDias = parseInt(grupo.dias ?? dadosFinanceiros.intervalo_dias) || 30
        dataAtual = calcularDataParcelaCavalo(dataAtual, intervaloDias)
      }
      const numeroParcela = index + 1
      parcelas.push({
        numero: numeroParcela,
        valor: valorPorUnidadePeso * grupo.peso,
        data: new Date(dataAtual),
        grupo: numeroParcela,
        peso: grupo.peso,
        descricao: `${numeroParcela}ª parcela (Peso ${grupo.peso})`,
      })
    })

    return parcelas
  }

  // Valor líquido (valor_total - desconto), usado no Preview do Pagamento —
  // mesma conta que gerarParcelasAutomaticas já faz internamente.
  const calcularValorLiquidoCavalo = () => {
    const vv = Number(dadosFinanceiros.valor_total) || 0
    let desconto = 0
    if (dadosFinanceiros.desconto_tipo === 'percentual' && dadosFinanceiros.desconto_percentual) {
      desconto = vv * parseFloat(dadosFinanceiros.desconto_percentual) / 100
    } else if (dadosFinanceiros.desconto_tipo === 'valor' && dadosFinanceiros.desconto_valor) {
      desconto = parseFloat(dadosFinanceiros.desconto_valor)
    }
    return vv - desconto
  }

  // Meios de Pagamento salvos (templates reutilizáveis) — porte de NovoLote.jsx
  const salvarMeioPagamento = async () => {
    if (!nomeMeioPagamento.trim()) {
      error('Digite um nome para o meio de pagamento')
      return
    }
    try {
      const meioPagamento = {
        nome: nomeMeioPagamento.trim(),
        tipo: 'PARCELADO',
        primeira_parcela_tipo: dadosFinanceiros.primeira_parcela_tipo,
        primeira_parcela_dias: dadosFinanceiros.primeira_parcela_dias,
        grupos_parcelas: dadosFinanceiros.grupos_parcelas,
        // Compat: telas antigas (ex: Gestão de Meios de Pagamento) ainda leem
        // esse campo pra montar a descrição resumida do modelo.
        intervalo_dias: dadosFinanceiros.grupos_parcelas[0]?.dias || 30,
      }
      const novoMeio = await meiosPagamentoService.criar(organizadorId, meioPagamento)
      setMeiosPagamento(prev => [...prev, novoMeio])
      setNomeMeioPagamento('')
      setShowSalvarMeioPagamento(false)
      success('Meio de pagamento salvo com sucesso!')
    } catch (err) {
      console.error('Erro ao salvar meio de pagamento:', err)
      error('Erro ao salvar meio de pagamento')
    }
  }

  const carregarMeioPagamento = (meio) => {
    setDadosFinanceiros(prev => ({
      ...prev,
      modalidade_pagamento: meio.tipo,
      dias_prazo: meio.dias_prazo || '',
      primeira_parcela_tipo: meio.primeira_parcela_tipo || 'A_VISTA',
      primeira_parcela_dias: meio.primeira_parcela_dias || '',
      grupos_parcelas: flattenGrupos((meio.grupos_parcelas || [{ quantidade: 3, peso: 1 }])
        .map(g => ({ ...g, dias: g.dias ?? meio.intervalo_dias ?? 30 }))),
    }))
    setShowMeiosPagamentoModal(false)
    success(`Meio de pagamento "${meio.nome}" carregado!`)
  }

  const excluirMeioPagamento = async (meioId) => {
    try {
      await meiosPagamentoService.excluir(organizadorId, meioId)
      setMeiosPagamento(prev => prev.filter(m => m.id !== meioId))
      success('Meio de pagamento excluído!')
    } catch (err) {
      console.error('Erro ao excluir meio de pagamento:', err)
      error('Erro ao excluir meio de pagamento')
    }
  }

  const handleFaturar = async () => {
    if (!compradorSelecionado) { error('Selecione o comprador'); return }
    if (!dadosFinanceiros.valor_total || parseFloat(dadosFinanceiros.valor_total) <= 0) {
      error('Informe o valor de venda'); return
    }
    if (!dadosFinanceiros.comissao_id) { error('Selecione a comissão'); return }
    if (compradoresIncompletos) {
      error('Preencha todos os co-compradores e feche a soma em 100%'); return
    }
    if (parcelarComissao && !comissaoParcelamento) {
      error('Configure o parcelamento da comissão de compra (ou desmarque a opção)'); return
    }

    try {
      setSaving(true)
      // Backend recalcula sozinho A_VISTA/PRAZO (ver lotes/routes.py, if modalidade
      // in ('A_VISTA','PRAZO')) — só PARCELADO precisa mandar as parcelas calculadas
      // no client, geradas por gerarParcelasAutomaticas().
      let dadosPagamento
      if (dadosFinanceiros.modalidade_pagamento === 'PRAZO') {
        dadosPagamento = { dias_prazo: parseInt(dadosFinanceiros.dias_prazo) || 0 }
      } else if (dadosFinanceiros.modalidade_pagamento === 'PARCELADO') {
        dadosPagamento = {
          primeira_parcela_tipo: dadosFinanceiros.primeira_parcela_tipo,
          primeira_parcela_dias: dadosFinanceiros.primeira_parcela_dias,
          grupos_parcelas: dadosFinanceiros.grupos_parcelas,
          parcelas_calculadas: gerarParcelasAutomaticas(),
        }
      } else {
        dadosPagamento = {}
      }

      await faturamentoService.faturarLote(organizadorId, loteId, {
        comprador_id: compradorSelecionado.id,
        fazenda_comprador_id: dadosFinanceiros.fazenda_comprador_id ? parseInt(dadosFinanceiros.fazenda_comprador_id) : null,
        valor_total: parseFloat(dadosFinanceiros.valor_total),
        comissao_id: parseInt(dadosFinanceiros.comissao_id),
        // Cada lado congela independente — ver LoteService._congelar_comissao_custom
        usa_comissao_padrao_venda: ignorarIndividualVenda,
        usa_comissao_padrao_compra: ignorarIndividualCompra,
        modalidade_pagamento: dadosFinanceiros.modalidade_pagamento,
        desconto_percentual: dadosFinanceiros.desconto_tipo === 'percentual' ? parseFloat(dadosFinanceiros.desconto_percentual || 0) : null,
        desconto_valor: dadosFinanceiros.desconto_tipo === 'valor' ? parseFloat(dadosFinanceiros.desconto_valor || 0) : null,
        quem_vendeu_id: dadosFinanceiros.quem_vendeu_id ? parseInt(dadosFinanceiros.quem_vendeu_id) : null,
        leiloeiro_id: dadosFinanceiros.fora_do_leilao ? null : (dadosFinanceiros.leiloeiro_id ? parseInt(dadosFinanceiros.leiloeiro_id) : null),
        fora_do_leilao: dadosFinanceiros.fora_do_leilao || false,
        assessor_id: dadosFinanceiros.assessor_id ? parseInt(dadosFinanceiros.assessor_id) : null,
        assessor_percentual: dadosFinanceiros.assessor_id && dadosFinanceiros.assessor_percentual !== '' && dadosFinanceiros.assessor_percentual != null
          ? parseFloat(dadosFinanceiros.assessor_percentual)
          : null,
        dados_pagamento: dadosPagamento,
        // Rateio multi-comprador (fluxo cavalo) — só vai quando há 2+; com 1
        // comprador fica null e o backend fatura no fluxo de comprador único.
        ...(compradoresPayload ? { compradores: compradoresPayload } : {}),
        // Parcelamento da comissão de compra (opcional, fluxo cavalo)
        comissao_parcelamento: parcelarComissao && comissaoParcelamento ? comissaoParcelamento : null,
      })
      success('Lote faturado com sucesso!')
      carregarLote()
    } catch (err) {
      console.error('Erro ao faturar lote:', err)
      error(err.response?.data?.detail || 'Erro ao faturar lote')
    } finally {
      setSaving(false)
    }
  }

  // Valor total da defesa: se o leilão fatura por parcela, o que foi digitado
  // é "por parcela" e multiplica pela quantidade de parcelas do meio de
  // pagamento padrão — senão, é o valor total direto.
  const valorTotalDefesa = () => {
    const valor = parseFloat((valorDefesa || '').replace(',', '.'))
    if (!valor || valor <= 0) return 0
    return defesaPorParcela ? valor * calcularQtdParcelasLeilao() : valor
  }

  // Preview em tempo real do valor de defesa — chama o backend (fonte da
  // verdade sobre tabela de faixas / percentual + sócios), com debounce.
  useEffect(() => {
    if (!showDefesaModal || !loteId) return
    const valorTotal = valorTotalDefesa()
    if (!valorTotal || valorTotal <= 0) { setPreviewDefesa(null); return }
    clearTimeout(previewDefesaDebounceRef.current)
    previewDefesaDebounceRef.current = setTimeout(async () => {
      setPreviewDefesaLoading(true)
      try {
        const resultado = await lotesService.previewDefesa(organizadorId, loteId, {
          valorTotal,
          comissaoId: defesaUsaPercentual ? comissaoDefesaId : null,
        })
        setPreviewDefesa(resultado)
      } catch {
        setPreviewDefesa(null)
      } finally {
        setPreviewDefesaLoading(false)
      }
    }, 350)
    return () => clearTimeout(previewDefesaDebounceRef.current)
  }, [showDefesaModal, valorDefesa, comissaoDefesaId, defesaUsaPercentual, defesaPorParcela, organizadorId, loteId])

  // Ao abrir o modal, pré-seleciona a comissão padrão do leilão (só usada em
  // modo percentual — em modo tabela o backend ignora comissao_id).
  const abrirModalDefesa = () => {
    setComissaoDefesaId(leilao?.comissao_padrao_id ? String(leilao.comissao_padrao_id) : '')
    setPreviewDefesa(null)
    // Traz o valor de venda digitado antes, se o de defesa ainda estiver vazio
    if (valorVendaInput && !valorDefesa) setValorDefesa(String(valorVendaInput))
    setShowDefesaModal(true)
    setTimeout(() => defesaInputRef.current?.focus(), 100)
  }

  // Defesa (mesmo mecanismo do lote de gado): fatura o lote com comprador =
  // vendedor — o backend detecta isso (comprador_id == lote.vendedor_id) e marca
  // status DEFESA automaticamente, calculando o valor pela tabela de faixas ou
  // pelo percentual de comissão, conforme a configuração do leilão.
  const marcarComoDefesa = async () => {
    const vendedorPrincipalId = vendedoresLote[0]?.vendedor_id
    if (!vendedorPrincipalId) { error('Cadastre o vendedor do lote antes de marcar defesa'); return }
    const valorTotal = valorTotalDefesa()
    if (!valorTotal || valorTotal <= 0) {
      error('Informe o valor da defesa'); return
    }
    try {
      setSaving(true)
      await faturamentoService.faturarLote(organizadorId, loteId, {
        comprador_id: parseInt(vendedorPrincipalId),
        fazenda_comprador_id: null,
        valor_total: valorTotal,
        comissao_id: defesaUsaPercentual && comissaoDefesaId ? parseInt(comissaoDefesaId) : null,
        modalidade_pagamento: 'A_VISTA',
        dados_pagamento: { primeira_parcela_tipo: 'A_VISTA', grupos_parcelas: [], parcelas_calculadas: [] },
      })
      success('Lote marcado como defesa com sucesso!')
      setShowDefesaModal(false)
      setValorDefesa('')
      setPreviewDefesa(null)
      carregarLote()
    } catch (err) {
      console.error('Erro ao marcar como defesa:', err)
      error(err.response?.data?.detail || 'Erro ao marcar como defesa')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  const stepId = steps[currentStep]?.id
  const jaFaturado = lote?.status === 'FATURADO'
  const emDefesa = lote?.status === 'DEFESA'
  // Lote "fechado" (faturado ou em defesa) — trava edição, mesma regra do
  // podeEditar() do gado (loteStatus !== 'FATURADO' && loteStatus !== 'DEFESA').
  const loteFechado = jaFaturado || emDefesa
  // Mesma condição que já habilitava o botão "Faturar Lote" dentro do step
  // Financeiro — agora também usada pro botão rápido no header.
  const podeFaturar = !loteFechado && valorVendaDefinido && !!compradorSelecionado
    && !!dadosFinanceiros.comissao_id && !!dadosFinanceiros.modalidade_pagamento

  return (
    <div className="page-content max-w-6xl mx-auto">
      <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 space-y-2">
        {toasts.map(t => (
          <Toast key={t.id} message={t.message} type={t.type} duration={t.duration} onClose={() => hideToast(t.id)} />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <button onClick={() => navigate('/lotes')} className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
              {isEditMode ? `Editar Lote — ${formData.numero_lote}` : 'Novo Lote'}
            </h1>
            {leilao && <p className="text-sm text-gray-500">{leilao.nome}</p>}
          </div>
        </div>

        {/* Ações rápidas no header — só no modo edição, mesmo padrão do lote de gado */}
        {isEditMode && (
          <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap pb-1 sm:pb-0">
            {/* NOVO — lote nunca faturado ainda */}
            {lote?.status === 'NOVO' && (
              <>
                {podeFaturar && (
                  <button type="button" onClick={handleFaturar} disabled={saving}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold rounded-lg bg-green-600 text-white hover:bg-green-700 active:scale-95 transition-all shadow-md ring-2 ring-green-300 disabled:opacity-50">
                    <DollarSign size={15} /> {saving ? 'Faturando...' : 'Faturar Lote'}
                  </button>
                )}
                <button type="button" onClick={() => { setShowValorVendaModal(true); setTimeout(() => valorVendaInputRef.current?.focus(), 100) }}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg bg-amber-500 text-white hover:bg-amber-600 active:scale-95 transition-all shadow-sm">
                  <DollarSign size={15} /> {valorVendaDefinido ? 'Alterar Venda' : 'Definir Venda'}
                </button>
                <button type="button" onClick={abrirModalDefesa}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg bg-red-500 text-white hover:bg-red-600 active:scale-95 transition-all shadow-sm">
                  <AlertTriangle size={15} /> Defesa
                </button>
              </>
            )}

            {/* EDITADO — já foi faturado antes e teve a negociação desfeita */}
            {lote?.status === 'EDITADO' && (
              <>
                {podeFaturar && (
                  <button type="button" onClick={handleFaturar} disabled={saving}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold rounded-lg bg-green-600 text-white hover:bg-green-700 active:scale-95 transition-all shadow-md ring-2 ring-green-300 disabled:opacity-50">
                    <DollarSign size={15} /> {saving ? 'Faturando...' : 'Faturar Lote'}
                  </button>
                )}
                <button type="button" onClick={() => { setShowValorVendaModal(true); setTimeout(() => valorVendaInputRef.current?.focus(), 100) }}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg bg-amber-500 text-white hover:bg-amber-600 active:scale-95 transition-all shadow-sm">
                  <DollarSign size={15} /> {valorVendaDefinido ? 'Alterar Venda' : 'Definir Venda'}
                </button>
                <button type="button" onClick={abrirModalDefesa}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg bg-red-500 text-white hover:bg-red-600 active:scale-95 transition-all shadow-sm">
                  <AlertTriangle size={15} /> Defesa
                </button>
                <button type="button" onClick={resetarLote}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg bg-gray-500 text-white hover:bg-gray-600 active:scale-95 transition-all shadow-sm">
                  <ArrowLeft size={15} /> Resetar
                </button>
              </>
            )}

            {loteFechado && (
              <>
                <button type="button" onClick={desfazerNegociacao} disabled={desfazendo}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 active:scale-95 transition-all shadow-sm disabled:opacity-50">
                  {desfazendo ? 'Desfazendo...' : (emDefesa ? 'Desfazer Defesa' : 'Desfazer')}
                </button>

                {/* Dropdown Contratos */}
                <div ref={contratosDropdownRef} className="relative inline-flex">
                  <button
                    type="button"
                    onClick={() => setShowContratosDropdown(v => !v)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 active:scale-95 transition-all shadow-sm"
                  >
                    <FileText size={16} /> Contratos ▾
                  </button>
                  {showContratosDropdown && (
                    <div className="absolute top-full left-0 mt-1 z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg min-w-[180px]">
                      <div className="py-1">
                        <div className="px-2 py-1">
                          <ContratoUmaVia lote={{ id: loteId, numero_lote: formData.numero_lote }} organizador={organizadorAtivo} />
                        </div>
                        <div className="px-2 py-1">
                          <ContratoNotaButton lote={{ id: loteId, numero_lote: formData.numero_lote }} organizador={organizadorAtivo} />
                        </div>
                        <div className="px-2 py-1">
                          <Contrato3ViasButton lote={{ id: loteId, numero_lote: formData.numero_lote }} organizador={organizadorAtivo} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Dropdown Notas */}
                <div ref={notasDropdownRef} className="relative inline-flex">
                  <button
                    type="button"
                    onClick={() => setShowNotasDropdown(v => !v)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg bg-green-600 text-white hover:bg-green-700 active:scale-95 transition-all shadow-sm"
                  >
                    <FileText size={16} /> Notas ▾
                  </button>
                  {showNotasDropdown && (
                    <div className="absolute top-full left-0 mt-1 z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg min-w-[180px]">
                      <div className="py-1">
                        <div className="px-2 py-1">
                          <NotaPromissoriaButton lote={{ id: loteId, numero_lote: formData.numero_lote }} organizador={organizadorAtivo} />
                        </div>
                        <div className="px-2 py-1">
                          <NotaCompromissoButton lote={{ id: loteId, numero_lote: formData.numero_lote }} organizador={organizadorAtivo} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Sempre disponível — Ficha do Lote (novo ou faturado) */}
            <FichaLeiloeiroButton
              lote={{ id: loteId, numero_lote: formData.numero_lote }}
              organizador={organizadorAtivo}
            />
          </div>
        )}
      </div>

      {/* Steps */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const Icon = step.icon
            const active = index === currentStep
            const done = index < currentStep
            return (
              <button
                key={step.id}
                onClick={() => setCurrentStep(index)}
                className="flex flex-col items-center gap-0.5 sm:gap-1 px-0.5 sm:px-2 flex-shrink-0 min-w-0"
              >
                <div className={`w-7 h-7 sm:w-9 sm:h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                  active ? 'bg-blue-600 text-white' : done ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
                }`}>
                  <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </div>
                <span className={`text-[10px] sm:text-xs font-medium truncate max-w-[54px] sm:max-w-none ${active ? 'text-blue-600' : 'text-gray-500'}`}>{step.title}</span>
              </button>
            )
          })}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-8">
        {loteFechado && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-800">
              <AlertTriangle size={20} />
              <h3 className="font-semibold">{emDefesa ? 'Lote em Defesa' : 'Lote Faturado'}</h3>
            </div>
            <p className="text-red-700 mt-1 text-sm">
              {emDefesa
                ? 'Este lote está marcado como defesa e não pode ser editado. Para fazer alterações, é necessário desfazer a defesa primeiro.'
                : 'Este lote já foi faturado e não pode ser editado. Para fazer alterações, é necessário desfazer a negociação primeiro.'}
            </p>
          </div>
        )}
        {stepId === 'lote' && (
          <fieldset disabled={loteFechado} className={`space-y-4 ${loteFechado ? 'opacity-60' : ''}`}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Número do Lote *</label>
              <input
                ref={setFieldRef('numero_lote')}
                type="text" value={formData.numero_lote}
                onChange={(e) => { setFormData({ ...formData, numero_lote: e.target.value }); marcarAlterado() }}
                onKeyDown={(e) => handleFieldKeyDown(e, 'curral')}
                className="input-rematei" placeholder="Ex: 01"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Curral</label>
                <input
                  ref={setFieldRef('curral')}
                  type="text" value={formData.curral}
                  onChange={(e) => { setFormData({ ...formData, curral: e.target.value }); marcarAlterado() }}
                  onKeyDown={(e) => handleFieldKeyDown(e, 'valor_minimo_unidade')}
                  className="input-rematei"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor Mínimo</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none">R$</span>
                  <input
                    ref={setFieldRef('valor_minimo_unidade')}
                    type="text" inputMode="numeric" value={formData.valor_minimo_unidade}
                    onChange={(e) => { setFormData({ ...formData, valor_minimo_unidade: formatarMoedaDigitos(e.target.value) }); marcarAlterado() }}
                    onKeyDown={(e) => handleFieldKeyDown(e, null)}
                    className="input-rematei pl-9" placeholder="0,00"
                  />
                </div>
              </div>
            </div>
          </fieldset>
        )}

        {stepId === 'animais' && (
          <AnimaisCavaloEditor
            animais={animais}
            onChange={(novos) => { setAnimais(novos); marcarAlterado() }}
            racas={racas}
            onCreateRaca={handleCriarRaca}
            tiposAnimal={tiposAnimal}
            onCreateTipo={handleCriarTipo}
            organizadorId={organizadorId}
            leilaoId={lote?.leilao_id || (leilaoIdParam ? parseInt(leilaoIdParam) : null)}
            disabled={loteFechado}
          />
        )}

        {stepId === 'vendedor' && (
          <fieldset disabled={loteFechado} className={`space-y-6 border-0 p-0 m-0 ${loteFechado ? 'opacity-60' : ''}`}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-3">
                <VendedoresCavaloEditor
                  vendedores={vendedoresLote}
                  onChange={(novos) => { setVendedoresLote(novos); marcarAlterado() }}
                  organizadorId={organizadorId}
                  disabled={loteFechado}
                />
                <p className="text-xs text-gray-500">
                  Vendedor não cadastrado?{' '}
                  <button type="button" onClick={() => setShowNovoVendedor(true)} className="text-rematei-blue hover:text-rematei-blue-dark font-medium hover:underline">
                    Cadastrar novo cliente
                  </button>
                </p>
              </div>

              {vendedoresLote[0]?.vendedor_id && (
                <div className="space-y-3 lg:border-l lg:border-gray-100 lg:pl-8">
                  <label className="block text-sm font-medium text-gray-700">Propriedade</label>
                  <label className="flex items-center gap-2 text-xs text-gray-500 -mt-2 cursor-pointer">
                    <input type="checkbox" checked={usarFazendaFicticia} onChange={(e) => { setUsarFazendaFicticia(e.target.checked); marcarAlterado() }} />
                    Propriedade de qualquer vendedor do lote — ou digite manualmente
                  </label>
                  {usarFazendaFicticia ? (
                    <input
                      ref={setFieldRef('fazenda')}
                      type="text" value={formData.fazenda_ficticia}
                      onChange={(e) => { setFormData({ ...formData, fazenda_ficticia: e.target.value }); marcarAlterado() }}
                      onKeyDown={(e) => handleFieldKeyDown(e, 'observacoes')}
                      className="input-rematei" placeholder="Nome da propriedade / haras"
                    />
                  ) : (
                    <select
                      ref={setFieldRef('fazenda')}
                      value={formData.fazenda_id}
                      onChange={(e) => { setFormData({ ...formData, fazenda_id: e.target.value }); marcarAlterado() }}
                      onKeyDown={(e) => handleFieldKeyDown(e, 'observacoes')}
                      className="input-rematei"
                    >
                      <option value="">Selecione a propriedade...</option>
                      {fazendas.map(f => (
                        <option key={f.id} value={f.id}>
                          {f.nome}{f._donoNome ? ` — ${f._donoNome}` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
              <textarea
                ref={setFieldRef('observacoes')}
                value={formData.observacoes}
                onChange={(e) => { setFormData({ ...formData, observacoes: e.target.value }); marcarAlterado() }}
                className="input-rematei" rows="3"
              />
              <p className="text-xs text-gray-400 mt-1">Vídeo, foto e link de cada animal ficam na aba Animais.</p>
            </div>
          </fieldset>
        )}

        {stepId === 'despesas' && (
          <DespesasLote loteId={loteId} organizadorId={organizadorId} loteStatus={lote?.status} fluxo="cavalo" />
        )}

        {stepId === 'financeiro' && (
          <div className="space-y-6">
            {emDefesa ? (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-5">
                <h4 className="font-semibold text-orange-800 mb-4 text-lg">Resumo da Defesa</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm text-orange-700">
                  <div><strong>Quantidade:</strong> {animais.length} {animais.length === 1 ? 'animal' : 'animais'}</div>
                  <div><strong>Valor Total:</strong> R$ {Number(lote?.valor_venda_total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
                  {defesaUsaPercentual ? (
                    <div>
                      <strong>Comissão Defesa ({Number(lote?.comissao_defesa || 0)}%):</strong> R$ {Number(lote?.valor_defesa || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </div>
                  ) : (
                    <div><strong>Valor da Defesa (tabela):</strong> R$ {Number(lote?.valor_defesa || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
                  )}
                  <div><strong>Vendedor/Comprador:</strong> {compradorSelecionado?.nome || lote?.vendedor?.nome || '—'}</div>
                </div>
              </div>
            ) : !loteFechado && !valorVendaDefinido ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                <DollarSign size={48} className="text-yellow-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">Defina o Valor de Venda</h3>
                <p className="text-yellow-700 mb-4">
                  Para prosseguir com os dados financeiros, defina primeiro o valor de venda.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setShowValorVendaModal(true)
                    setTimeout(() => valorVendaInputRef.current?.focus(), 100)
                  }}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  Definir Valor de Venda
                </button>
              </div>
            ) : (
              <fieldset disabled={loteFechado} className={`space-y-6 border-0 p-0 m-0 ${loteFechado ? 'opacity-70' : ''}`}>
                {/* Comprador + Fazenda / Preview do Comprador */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="relative">
                      <label className="flex items-center justify-between text-sm font-medium text-gray-700 mb-1">
                        <span>Comprador *</span>
                        <button
                          type="button"
                          onClick={() => setShowNovoComprador(true)}
                          className="text-blue-600 hover:text-blue-800 p-1 rounded transition-colors"
                          title="Criar novo comprador"
                        >
                          <Plus size={16} />
                        </button>
                      </label>
                      <div className="relative">
                        <input
                          ref={setFieldRef('comprador')}
                          type="text" value={searchComprador}
                          onChange={(e) => buscarComprador(e.target.value)}
                          onKeyDown={(e) => handleAutocompleteKeyDown(e, opcoesComprador, selecionarComprador, 'comissao_id')}
                          className={`input-rematei pr-9 ${
                            compradorSelecionado
                              ? (compradorSelecionado.restrito_compra ? 'border-orange-500 bg-orange-50' : 'border-green-500 bg-green-50')
                              : ''
                          }`}
                          placeholder="Digite o nome do comprador..."
                        />
                        {compradorSelecionado && (
                          <Check size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600" />
                        )}
                      </div>
                      {opcoesComprador.length > 0 && (
                        <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                          {opcoesComprador.map(c => (
                            <li
                              key={c.id}
                              onMouseDown={() => selecionarComprador(c)}
                              className={`px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer ${c.restrito_compra ? 'bg-red-50/50' : ''}`}
                            >
                              <div className="font-medium flex items-center gap-2 flex-wrap">
                                {c.nome}
                                <span className="text-xs font-normal text-gray-400">#{c.id}</span>
                                {c.restrito_compra && (
                                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-semibold">⚠️ RESTRITO</span>
                                )}
                                {c.is_socio && (
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full font-semibold">👥 SÓCIO</span>
                                )}
                              </div>
                              {c.cpf_cnpj && <div className="text-xs text-gray-500 mt-0.5">{c.cpf_cnpj}</div>}
                              {(() => {
                                const termo = searchComprador.toLowerCase()
                                const fazendasMatch = (c.fazendas || []).filter(f => f.nome && contem(f.nome, termo))
                                if (fazendasMatch.length === 0) return null
                                return (
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {fazendasMatch.map(f => (
                                      <span key={f.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                                        🏠 {f.nome}{f.cidade ? ` · ${f.cidade}` : ''}
                                      </span>
                                    ))}
                                  </div>
                                )
                              })()}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {/* Modal de Comprador Restrito */}
                    {compradorRestritoModal && (
                      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setCompradorRestritoModal(null)} />
                        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                          <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-5 flex items-center gap-3">
                            <div className="p-2 bg-white/20 rounded-xl">
                              <AlertTriangle className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h2 className="text-base font-bold text-white">Comprador Restrito</h2>
                              <p className="text-red-100 text-xs">Este cliente possui restrição de compra</p>
                            </div>
                          </div>
                          <div className="p-6 space-y-4">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{compradorRestritoModal.nome}</p>
                              {compradorRestritoModal.cpf_cnpj && (
                                <p className="text-xs text-gray-500">{compradorRestritoModal.cpf_cnpj}</p>
                              )}
                            </div>
                            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                              <p className="text-sm font-semibold text-red-800 mb-1">Motivo da restrição:</p>
                              <p className="text-sm text-red-700">
                                {compradorRestritoModal.restrito_compra_motivo || 'Não informado'}
                              </p>
                            </div>
                            <div className="flex gap-3 pt-1">
                              <button
                                type="button"
                                onClick={() => setCompradorRestritoModal(null)}
                                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors"
                              >
                                Cancelar
                              </button>
                              <button
                                type="button"
                                onClick={confirmarCompradorRestrito}
                                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl transition-colors"
                              >
                                Assumir risco
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Fazenda do Comprador */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                        <MapPin size={14} className="text-green-500" />
                        Fazenda do Comprador
                      </label>
                      <select
                        value={dadosFinanceiros.fazenda_comprador_id}
                        onChange={(e) => setDadosFinanceiros({ ...dadosFinanceiros, fazenda_comprador_id: e.target.value })}
                        className="input-rematei"
                        disabled={!compradorSelecionado}
                      >
                        <option value="">Selecione a fazenda...</option>
                        {fazendasComprador.map(f => (
                          <option key={f.id} value={f.id}>
                            {f.nome}{f.id.toString() === fazendasComprador[fazendasComprador.length - 1]?.id.toString() ? ' (Padrão)' : ''}
                          </option>
                        ))}
                      </select>
                      {fazendasComprador.length === 0 && compradorSelecionado && (
                        <p className="text-xs text-gray-500 mt-1">Nenhuma fazenda cadastrada para este comprador</p>
                      )}
                    </div>
                  </div>

                  {/* Preview do Comprador */}
                  <div>
                    {compradorSelecionado ? (
                      <div className={`p-4 border rounded-lg ${compradorSelecionado.is_socio ? 'bg-blue-50 border-blue-200' : 'bg-purple-50 border-purple-200'}`}>
                        <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${compradorSelecionado.is_socio ? 'text-blue-800' : 'text-purple-800'}`}>
                          <User size={16} />
                          Preview do Comprador
                        </h4>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className={`font-semibold ${compradorSelecionado.is_socio ? 'text-blue-800' : 'text-purple-800'}`}>
                              {compradorSelecionado.nome}
                              <span className="ml-1.5 text-xs font-normal opacity-50">#{compradorSelecionado.id}</span>
                            </span>
                            {compradorSelecionado.is_socio && (
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-semibold">👥 SÓCIO</span>
                            )}
                          </div>
                          {compradorSelecionado.cpf_cnpj && (
                            <div className="flex justify-between text-sm">
                              <span className={compradorSelecionado.is_socio ? 'text-blue-700' : 'text-purple-700'}>CPF/CNPJ:</span>
                              <span className={`font-medium ${compradorSelecionado.is_socio ? 'text-blue-800' : 'text-purple-800'}`}>{compradorSelecionado.cpf_cnpj}</span>
                            </div>
                          )}
                          {compradorSelecionado.telefone && (
                            <div className="flex justify-between text-sm">
                              <span className={compradorSelecionado.is_socio ? 'text-blue-700' : 'text-purple-700'}>Telefone:</span>
                              <span className={`font-medium ${compradorSelecionado.is_socio ? 'text-blue-800' : 'text-purple-800'}`}>{compradorSelecionado.telefone}</span>
                            </div>
                          )}
                          {compradorSelecionado.cidade && (
                            <div className="flex justify-between text-sm">
                              <span className={compradorSelecionado.is_socio ? 'text-blue-700' : 'text-purple-700'}>Cidade:</span>
                              <span className={`font-medium ${compradorSelecionado.is_socio ? 'text-blue-800' : 'text-purple-800'}`}>{compradorSelecionado.cidade}/{compradorSelecionado.estado}</span>
                            </div>
                          )}
                          {compradorSelecionado.is_socio && (
                            <div className="mt-2 pt-2 border-t border-blue-200 text-xs text-blue-600 font-medium">
                              ✓ Comissão de sócio será aplicada
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg h-full flex items-center justify-center">
                        <div className="text-center text-gray-500">
                          <User size={24} className="mx-auto mb-2 opacity-50" />
                          <p className="text-sm">Selecione um comprador para ver o preview</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Co-compradores (rateio — fluxo cavalo) */}
                {compradorSelecionado && !emDefesa && (
                  <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <CompradoresCavaloEditor
                      compradores={compradoresLote}
                      onChange={handleCompradoresChange}
                      organizadorId={organizadorId}
                      disabled={loteFechado}
                    />
                  </div>
                )}

                {/* Comissão + Preview */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="flex items-center justify-between text-sm font-medium text-gray-700 mb-1">
                      Comissão *
                      <button
                        type="button"
                        onClick={() => setShowNovaComissaoModal(true)}
                        className="text-blue-600 hover:text-blue-800 p-0.5 rounded transition-colors"
                        title="Criar nova comissão"
                      >
                        <Plus size={16} />
                      </button>
                    </label>
                    <select
                      ref={setFieldRef('comissao_id')}
                      value={dadosFinanceiros.comissao_id}
                      onChange={(e) => setDadosFinanceiros({ ...dadosFinanceiros, comissao_id: e.target.value })}
                      onKeyDown={(e) => handleFieldKeyDown(e, 'desconto_percentual')}
                      className="input-rematei"
                    >
                      <option value="">Selecione...</option>
                      {comissoes.map(c => (
                        <option key={c.id} value={c.id}>{c.nome} {c.tipo_comissao === 'socios' && '(Sócios)'}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    {comissaoCalculada ? (
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h4 className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                          <DollarSign size={16} />
                          Preview da Comissão
                        </h4>
                        {(() => {
                          const cartoes = [...getCartoesLado('compra'), ...getCartoesLado('venda')]
                          const cols = cartoes.length >= 4 ? 'grid-cols-4' : cartoes.length === 3 ? 'grid-cols-3' : 'grid-cols-2'
                          const fmt = (v) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
                          return (
                            <div className="text-xs text-blue-700">
                              <div className={`grid ${cols} gap-1.5`}>
                                {cartoes.map(renderCartaoComissao)}
                              </div>
                              <div className="grid grid-cols-2 gap-3 mt-1.5 text-center">
                                <div className="text-xs font-medium text-blue-600">Compra: R$ {fmt(valorLadoComissao('compra'))}</div>
                                <div className="text-xs font-medium text-blue-600">Venda: R$ {fmt(valorLadoComissao('venda'))}</div>
                              </div>
                            </div>
                          )
                        })()}
                        {confirmacaoTroca && (
                          <div className="mt-2 p-3 rounded-lg border border-red-300 bg-red-50 text-xs text-red-800">
                            <div className="font-medium mb-2">
                              Trocar de <strong>{confirmacaoTroca.pctAtual.toFixed(2)}%</strong> para{' '}
                              <strong>{Number(confirmacaoTroca.pctNovo).toFixed(2)}% ({confirmacaoTroca.label})</strong> — confirma a troca?
                            </div>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => setConfirmacaoTroca(null)}
                                className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-700 font-medium"
                              >
                                Cancelar
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  confirmacaoTroca.setIgnorando(confirmacaoTroca.destino)
                                  if (loteId && organizadorId) {
                                    faturamentoService.logTrocaComissao(organizadorId, loteId, {
                                      lado: confirmacaoTroca.lado,
                                      pct_atual: confirmacaoTroca.pctAtual,
                                      pct_novo: confirmacaoTroca.pctNovo,
                                      label_novo: confirmacaoTroca.label,
                                    }).catch(() => {})
                                  }
                                  setConfirmacaoTroca(null)
                                }}
                                className="px-3 py-1 rounded bg-red-600 text-white font-medium"
                              >
                                Confirmar troca
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg h-full flex items-center justify-center">
                        <div className="text-center text-gray-500">
                          <DollarSign size={24} className="mx-auto mb-2 opacity-50" />
                          <p className="text-sm">Selecione uma comissão para ver o preview</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Parcelamento da comissão de compra (opcional) ─────────────── */}
                {compradorSelecionado && !emDefesa && (() => {
                  const vv = Number(dadosFinanceiros.valor_total) || 0
                  const cBase = comissoes.find(c => c.id.toString() === dadosFinanceiros.comissao_id)
                  const cEf = comissaoCalculada || cBase
                  const ccPct = comissaoCustom.compra != null ? comissaoCustom.compra : Number(cEf?.comissao_compra || 0)
                  const comissaoCompraValor = Math.round(vv * ccPct) / 100
                  const n = comissaoParcelamento?.parcelas?.length || 0
                  return (
                    <div className="mt-4 p-4 bg-white border border-gray-200 rounded-lg">
                      <label className={`flex items-start gap-2.5 ${loteFechado ? 'cursor-default' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={parcelarComissao}
                          disabled={loteFechado || comissaoCompraValor <= 0}
                          onChange={e => {
                            const v = e.target.checked
                            setParcelarComissao(v)
                            if (v) setShowParcelamentoComissao(true)
                            else setComissaoParcelamento(null)
                          }}
                          className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-800">Parcelar a comissão de compra no contrato</span>
                          <p className="text-xs text-gray-500">
                            Adiciona ao contrato a tabela de parcelas + uma nota promissória própria da comissão
                            {comissaoCompraValor > 0 && <> — total <strong className="tabular-nums">{comissaoCompraValor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong></>}
                          </p>
                        </div>
                      </label>

                      {parcelarComissao && comissaoParcelamento && (
                        <div className="mt-3 flex items-center justify-between rounded-lg bg-blue-50 border border-blue-200 px-3 py-2">
                          <span className="text-xs text-blue-700">
                            {comissaoParcelamento.entrada_valor ? `Entrada ${Number(comissaoParcelamento.entrada_valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} + ` : ''}
                            {n} parcela{n !== 1 ? 's' : ''}
                          </span>
                          {!loteFechado && (
                            <button type="button" onClick={() => setShowParcelamentoComissao(true)}
                              className="text-xs font-medium text-blue-700 hover:text-blue-900 underline">
                              Editar
                            </button>
                          )}
                        </div>
                      )}
                      {parcelarComissao && !comissaoParcelamento && !loteFechado && (
                        <button type="button" onClick={() => setShowParcelamentoComissao(true)}
                          className="mt-3 text-xs font-medium text-blue-700 hover:text-blue-900 underline">
                          Configurar parcelamento →
                        </button>
                      )}

                      {showParcelamentoComissao && (
                        <ParcelamentoComissaoModal
                          comissaoTotal={comissaoCompraValor}
                          initial={comissaoParcelamento}
                          onClose={() => {
                            setShowParcelamentoComissao(false)
                            if (!comissaoParcelamento) setParcelarComissao(false)
                          }}
                          onSalvar={(dados) => {
                            setComissaoParcelamento(dados)
                            setParcelarComissao(true)
                            setShowParcelamentoComissao(false)
                          }}
                        />
                      )}
                    </div>
                  )
                })()}

                {/* Resumo da Venda — regra única do cavalo: comissão de compra e venda
                    NÃO entram no valor líquido do vendedor aqui (o vendedor paga comissão
                    de venda + despesas + taxa de inscrição depois, via Gestão Financeira —
                    ver gestao/routes.py, branch FluxoComissao.cavalo). São mostradas só
                    como informação. */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 mb-3">Resumo da Venda</h4>
                  {(() => {
                    const vv = Number(dadosFinanceiros.valor_total) || 0
                    const comissaoBase = comissoes.find(c => c.id.toString() === dadosFinanceiros.comissao_id)
                    const comissaoEfetiva = comissaoCalculada || comissaoBase
                    const ccPct = comissaoCustom.compra != null ? comissaoCustom.compra : Number(comissaoEfetiva?.comissao_compra || 0)
                    const cvPct = comissaoCustom.venda != null ? comissaoCustom.venda : Number(comissaoEfetiva?.comissao_venda || 0)
                    const ccVal = vv * ccPct / 100
                    const cvVal = vv * cvPct / 100
                    let desconto = 0
                    if (dadosFinanceiros.desconto_tipo === 'percentual' && dadosFinanceiros.desconto_percentual) {
                      desconto = vv * parseFloat(dadosFinanceiros.desconto_percentual) / 100
                    } else if (dadosFinanceiros.desconto_tipo === 'valor' && dadosFinanceiros.desconto_valor) {
                      desconto = parseFloat(dadosFinanceiros.desconto_valor)
                    }
                    const valorRegistrar = vv - desconto
                    const fmt = (v) => Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
                    const cenario = comissaoCalculada?.cenario
                    return (
                      <div className="space-y-3">
                        {cenario && (
                          <div className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded inline-block">
                            Cenário: {cenario}
                          </div>
                        )}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm text-green-700">
                          <div><strong>Quantidade:</strong> {animais.length} {animais.length === 1 ? 'animal' : 'animais'}</div>
                          <div><strong>Valor de Venda:</strong> R$ {fmt(vv)}</div>
                          <div><strong>Com. Compra ({ccPct}%):</strong> R$ {fmt(ccVal)}</div>
                          <div><strong>Com. Venda ({cvPct}%):</strong> R$ {fmt(cvVal)}</div>
                          {leilao?.taxa_inscricao_valor > 0 && (
                            <div><strong>Taxa de Inscrição:</strong> R$ {fmt(leilao.taxa_inscricao_valor)}</div>
                          )}
                          {desconto > 0 && (
                            <div className="text-orange-700"><strong>Desconto:</strong> R$ {fmt(desconto)}</div>
                          )}
                          <div className="col-span-2 sm:col-span-3 pt-2 border-t border-green-200">
                            <strong className="text-green-900 text-base">Valor a Registrar: R$ {fmt(valorRegistrar)}</strong>
                          </div>
                        </div>
                      </div>
                    )
                  })()}
                </div>

                {/* Quem Vendeu + Leiloeiro + Modalidade de Pagamento + Configuração de
                    Parcelamento — layout portado do gado (NovoLote.jsx, case 5): um único
                    grid de 2 colunas, coluna 1 com os campos empilhados e coluna 2 com o
                    card "Preview do Pagamento" + Leiloeiro logo abaixo. */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    {(() => {
                      const moduloBaseAtivo = organizadorAtivo?.produtos_premium?.includes('quem_vendeu_trouxe')
                      if (!moduloBaseAtivo && !quemVendeuTravado) return null
                      return (
                        <div>
                          {moduloBaseAtivo ? (
                            <PisteiroSelect
                              organizadorId={organizadorId}
                              value={dadosFinanceiros.quem_vendeu_id}
                              onChange={(val) => setDadosFinanceiros(prev => ({ ...prev, quem_vendeu_id: val }))}
                              disabled={quemVendeuTravado}
                              label="Quem Vendeu"
                            />
                          ) : (
                            <div>
                              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                                <Users size={14} className="text-purple-500" /> Quem Vendeu
                              </label>
                              <div className="input-rematei bg-gray-100 text-gray-700 cursor-not-allowed">
                                REMATEI
                              </div>
                            </div>
                          )}
                          {quemVendeuTravado && (
                            <p className="text-xs text-gray-500 mt-1">
                              Definido automaticamente pelo módulo Pisteiro remoto — não pode ser alterado.
                            </p>
                          )}
                        </div>
                      )
                    })()}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Modalidade de Pagamento</label>
                      <select
                        ref={setFieldRef('modalidade_pagamento')}
                        value={dadosFinanceiros.modalidade_pagamento}
                        onChange={(e) => {
                          const value = e.target.value
                          const meioSelecionado = meiosPagamento.find(m => m.id.toString() === value)
                          if (meioSelecionado) {
                            carregarMeioPagamento(meioSelecionado)
                            return
                          }
                          setDadosFinanceiros(prev => ({ ...prev, modalidade_pagamento: value, dias_prazo: '' }))
                        }}
                        onKeyDown={(e) => handleFieldKeyDown(e, dadosFinanceiros.modalidade_pagamento === 'PRAZO' ? 'dias_prazo' : null)}
                        className="input-rematei"
                      >
                        <option value="A_VISTA">À Vista</option>
                        <option value="PRAZO">Prazo (dias corridos)</option>
                        <option value="PARCELADO">Parcelado</option>
                        {meiosPagamento.length > 0 && (
                          <>
                            <option disabled>──────────────</option>
                            <option disabled>Meios Salvos:</option>
                            {meiosPagamento.map(meio => (
                              <option key={meio.id} value={meio.id}>{meio.nome}</option>
                            ))}
                          </>
                        )}
                      </select>
                      {meiosPagamento.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setShowMeiosPagamentoModal(true)}
                          className="text-xs text-rematei-blue hover:text-rematei-blue-dark mt-1"
                        >
                          Meus Meios de Pagamento
                        </button>
                      )}
                    </div>

                    {dadosFinanceiros.modalidade_pagamento === 'PRAZO' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Dias de Prazo</label>
                        <input
                          ref={setFieldRef('dias_prazo')}
                          type="number" value={dadosFinanceiros.dias_prazo}
                          onChange={(e) => setDadosFinanceiros({ ...dadosFinanceiros, dias_prazo: e.target.value })}
                          onKeyDown={(e) => handleFieldKeyDown(e, null)}
                          className="input-rematei"
                        />
                      </div>
                    )}

                    {/* Configuração de Parcelamento Avançado */}
                    {dadosFinanceiros.modalidade_pagamento === 'PARCELADO' && (
                      <div className="space-y-4 border border-gray-200 rounded-lg p-4">
                        {/* Vencimento da 1ª Parcela */}
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                            Vencimento da 1ª Parcela
                          </label>
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
                              <button
                                type="button"
                                onClick={() => setDadosFinanceiros(prev => ({ ...prev, primeira_parcela_tipo: 'A_VISTA', primeira_parcela_dias: '' }))}
                                className={`px-3 py-2 font-medium transition-colors ${dadosFinanceiros.primeira_parcela_tipo === 'A_VISTA' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                              >
                                À Vista
                              </button>
                              <button
                                type="button"
                                onClick={() => setDadosFinanceiros(prev => ({ ...prev, primeira_parcela_tipo: 'DIAS', primeira_parcela_dias: prev.primeira_parcela_dias || '30' }))}
                                className={`px-3 py-2 font-medium transition-colors border-l border-gray-300 ${dadosFinanceiros.primeira_parcela_tipo === 'DIAS' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                              >
                                A prazo
                              </button>
                            </div>
                            {dadosFinanceiros.primeira_parcela_tipo === 'DIAS' && (
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  value={dadosFinanceiros.primeira_parcela_dias}
                                  onChange={(e) => setDadosFinanceiros(prev => ({ ...prev, primeira_parcela_dias: e.target.value }))}
                                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  placeholder="30"
                                  min="1"
                                />
                                <span className="text-sm text-gray-500">dias</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Atalhos */}
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                            Atalhos
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {[
                              { label: '3x iguais', grupos: [{ quantidade: 3, peso: 1, dias: 30 }], primeira_tipo: 'A_VISTA', primeira_dias: '' },
                              { label: '6x iguais', grupos: [{ quantidade: 6, peso: 1, dias: 30 }], primeira_tipo: 'A_VISTA', primeira_dias: '' },
                              { label: '12x iguais', grupos: [{ quantidade: 12, peso: 1, dias: 30 }], primeira_tipo: 'A_VISTA', primeira_dias: '' },
                              { label: '5D + 30S', grupos: [{ quantidade: 5, peso: 2, dias: 30 }, { quantidade: 30, peso: 1, dias: 30 }], primeira_tipo: 'DIAS', primeira_dias: '30' },
                            ].map((preset) => (
                              <button
                                key={preset.label}
                                type="button"
                                onClick={() => setDadosFinanceiros(prev => ({
                                  ...prev,
                                  grupos_parcelas: flattenGrupos(preset.grupos),
                                  primeira_parcela_tipo: preset.primeira_tipo,
                                  primeira_parcela_dias: preset.primeira_dias,
                                }))}
                                className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-full border border-gray-200 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300 transition-colors"
                              >
                                {preset.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Parcelas */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Parcelas</label>
                              <p className="text-xs text-gray-400 mt-0.5">1 linha por parcela — digite os dias desde a anterior ou escolha a data</p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => setShowSalvarMeioPagamento(true)}
                                className="text-xs px-3 py-1.5 bg-orange-50 text-orange-600 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors font-medium"
                              >
                                Salvar como modelo
                              </button>
                              <button
                                type="button"
                                onClick={() => setDadosFinanceiros(prev => ({ ...prev, grupos_parcelas: [...prev.grupos_parcelas, { quantidade: 1, peso: 1, dias: 30 }] }))}
                                className="text-xs px-3 py-1.5 bg-blue-50 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-1 font-medium"
                              >
                                <Plus size={12} /> Parcela
                              </button>
                            </div>
                          </div>

                          <div className="space-y-2 max-h-[28rem] overflow-y-auto pr-1">
                            {(() => {
                              const parcelasPreview = gerarParcelasAutomaticas()
                              return dadosFinanceiros.grupos_parcelas.map((grupo, index) => {
                              const totalUnidades = dadosFinanceiros.grupos_parcelas.reduce((acc, g) => acc + (g.quantidade * g.peso), 0)
                              const valorLiquidoPreview = calcularValorLiquidoCavalo()
                              const valorPorUnidade = totalUnidades > 0 ? valorLiquidoPreview / totalUnidades : 0
                              const valorParcela = valorPorUnidade * grupo.peso
                              const dataDestaParcela = parcelasPreview[index]?.data
                              const dataAnterior = index > 0 ? parcelasPreview[index - 1]?.data : null
                              const emModoData = !!parcelasEmModoData[index]
                              return (
                                <div key={index} className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                <div className="flex items-center gap-3">
                                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center">
                                    {index + 1}
                                  </div>
                                  {grupo.peso > 1 && (
                                    <div className="flex-1 min-w-0">
                                      <label className="block text-xs text-gray-500 mb-1" title="Múltiplo 1 = simples, 2 = duplo, etc.">Múltiplo</label>
                                      <input
                                        type="number"
                                        value={grupo.peso}
                                        onChange={(e) => {
                                          const novosGrupos = [...dadosFinanceiros.grupos_parcelas]
                                          novosGrupos[index] = { ...novosGrupos[index], peso: parseInt(e.target.value) || 1 }
                                          setDadosFinanceiros(prev => ({ ...prev, grupos_parcelas: novosGrupos }))
                                        }}
                                        className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm text-center font-semibold focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        min="1"
                                      />
                                    </div>
                                  )}

                                  {/* Vencimento: 1ª parcela vem do controle acima; as demais têm
                                      dias-desde-anterior OU data direta, alternáveis */}
                                  {index === 0 ? (
                                    <div className="flex-1 min-w-0 text-xs text-gray-400 italic">
                                      Vencimento definido acima (1ª Parcela)
                                    </div>
                                  ) : emModoData ? (
                                    <div className="flex-1 min-w-0">
                                      <label className="block text-xs text-gray-500 mb-1">Data de vencimento</label>
                                      <input
                                        type="date"
                                        value={dataDestaParcela ? dataDestaParcela.toISOString().split('T')[0] : ''}
                                        onChange={(e) => {
                                          if (!e.target.value || !dataAnterior) return
                                          const novaData = new Date(e.target.value + 'T00:00:00')
                                          const diffDias = Math.round((novaData - dataAnterior) / (1000 * 60 * 60 * 24))
                                          const novosGrupos = [...dadosFinanceiros.grupos_parcelas]
                                          novosGrupos[index] = { ...novosGrupos[index], dias: Math.max(1, diffDias) }
                                          setDadosFinanceiros(prev => ({ ...prev, grupos_parcelas: novosGrupos }))
                                        }}
                                        className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      />
                                    </div>
                                  ) : (
                                    <div className="flex-1 min-w-0">
                                      <label className="block text-xs text-gray-500 mb-1">Dias desde a anterior</label>
                                      <input
                                        type="number"
                                        value={grupo.dias ?? 30}
                                        placeholder="30"
                                        onChange={(e) => {
                                          const novosGrupos = [...dadosFinanceiros.grupos_parcelas]
                                          novosGrupos[index] = { ...novosGrupos[index], dias: parseInt(e.target.value) || 1 }
                                          setDadosFinanceiros(prev => ({ ...prev, grupos_parcelas: novosGrupos }))
                                        }}
                                        className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm text-center font-semibold focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        min="1"
                                      />
                                    </div>
                                  )}

                                  {/* Alterna dias <-> data (só faz sentido a partir da 2ª parcela) */}
                                  {index > 0 && (
                                    <button
                                      type="button"
                                      onClick={() => setParcelasEmModoData(prev => ({ ...prev, [index]: !prev[index] }))}
                                      title={emModoData ? 'Digitar quantidade de dias' : 'Escolher data no calendário'}
                                      className="flex-shrink-0 self-end mb-1.5 p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                    >
                                      <Calendar size={14} />
                                    </button>
                                  )}

                                  {valorLiquidoPreview > 0 && (
                                    <div className="flex-shrink-0 text-right">
                                      <div className="text-xs text-gray-400">por parcela</div>
                                      <div className="text-sm font-bold text-green-700">
                                        R$ {valorParcela.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </div>
                                    </div>
                                  )}
                                  {dadosFinanceiros.grupos_parcelas.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const novosGrupos = dadosFinanceiros.grupos_parcelas.filter((_, i) => i !== index)
                                        setDadosFinanceiros(prev => ({ ...prev, grupos_parcelas: novosGrupos }))
                                      }}
                                      className="flex-shrink-0 p-1.5 text-red-400 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
                                    >
                                      <X size={14} />
                                    </button>
                                  )}
                                </div>

                                {/* Vencimento calculado + atalho de múltiplo */}
                                <div className="mt-2 pl-10 flex items-center gap-2 flex-wrap">
                                  {dataDestaParcela && (
                                    <span className="text-xs text-gray-400">
                                      vence em <b className="text-gray-600">{dataDestaParcela.toLocaleDateString('pt-BR')}</b>
                                    </span>
                                  )}
                                  {grupo.peso === 1 && (
                                    <>
                                      {dataDestaParcela && <span className="text-xs text-gray-300">·</span>}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const novosGrupos = [...dadosFinanceiros.grupos_parcelas]
                                          novosGrupos[index] = { ...novosGrupos[index], peso: 2 }
                                          setDadosFinanceiros(prev => ({ ...prev, grupos_parcelas: novosGrupos }))
                                        }}
                                        className="text-xs text-gray-400 hover:text-blue-600 underline underline-offset-2 whitespace-nowrap"
                                        title="Definir múltiplo (parcelas com valor diferente)"
                                      >
                                        + múltiplo
                                      </button>
                                    </>
                                  )}
                                </div>
                                </div>
                              )
                              })
                            })()}
                          </div>

                          {dadosFinanceiros.grupos_parcelas.length > 0 && (
                            <div className="mt-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg flex justify-between items-center flex-wrap gap-1">
                              <span className="text-xs text-blue-600 font-medium">
                                Total: {dadosFinanceiros.grupos_parcelas.reduce((acc, g) => acc + g.quantidade, 0)} parcelas
                              </span>
                              {(() => {
                                const parcelasPreview = gerarParcelasAutomaticas()
                                if (parcelasPreview.length === 0) return null
                                return (
                                  <span className="text-xs text-blue-600">
                                    Última: {parcelasPreview[parcelasPreview.length - 1].data.toLocaleDateString('pt-BR')}
                                  </span>
                                )
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Preview do Pagamento + Leiloeiro */}
                  <div>
                    {dadosFinanceiros.modalidade_pagamento ? (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <h4 className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2">
                          <DollarSign size={16} />
                          Preview do Pagamento
                        </h4>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-green-700">Modalidade:</span>
                            <span className="font-semibold text-green-800">
                              {dadosFinanceiros.modalidade_pagamento === 'A_VISTA' && 'À Vista'}
                              {dadosFinanceiros.modalidade_pagamento === 'PRAZO' && `Prazo ${dadosFinanceiros.dias_prazo || '?'} dias`}
                              {dadosFinanceiros.modalidade_pagamento === 'PARCELADO' && (() => {
                                const totalParcelas = dadosFinanceiros.grupos_parcelas.reduce((acc, grupo) => acc + grupo.quantidade, 0)
                                return `Parcelado - ${totalParcelas} parcelas`
                              })()}
                            </span>
                          </div>

                          {/* Data de pagamento (À Vista) */}
                          {dadosFinanceiros.modalidade_pagamento === 'A_VISTA' && (
                            <div className="mt-3 pt-3 border-t border-green-200">
                              <div className="text-xs text-green-600 mb-1">Data de Pagamento</div>
                              <div className="text-sm text-green-700">
                                {(configuracoesFaturamento?.data_base_calculo === 'DATA_LEILAO' && leilao?.data_leilao ? new Date(leilao.data_leilao) : new Date()).toLocaleDateString('pt-BR')}
                              </div>
                            </div>
                          )}

                          {/* Desconto */}
                          {dadosFinanceiros.desconto_tipo && (
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-orange-600">
                                Desconto ({dadosFinanceiros.desconto_tipo === 'percentual' ? `${dadosFinanceiros.desconto_percentual || 0}%` : 'fixo'}):
                              </span>
                              <span className="font-semibold text-orange-700">
                                - R$ {(() => {
                                  const vv = Number(dadosFinanceiros.valor_total) || 0
                                  if (dadosFinanceiros.desconto_tipo === 'percentual' && dadosFinanceiros.desconto_percentual) {
                                    return (vv * parseFloat(dadosFinanceiros.desconto_percentual) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
                                  } else if (dadosFinanceiros.desconto_tipo === 'valor' && dadosFinanceiros.desconto_valor) {
                                    return parseFloat(dadosFinanceiros.desconto_valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
                                  }
                                  return '0,00'
                                })()}
                              </span>
                            </div>
                          )}

                          <div className="flex justify-between items-center">
                            <span className="text-sm text-green-700">Valor Líquido:</span>
                            <span className="font-bold text-green-800 text-lg">
                              R$ {calcularValorLiquidoCavalo().toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                          </div>

                          {/* Cronograma (Parcelado) */}
                          {dadosFinanceiros.modalidade_pagamento === 'PARCELADO' && (() => {
                            const parcelasCalculadas = gerarParcelasAutomaticas()
                            return parcelasCalculadas.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-green-200">
                                <div className="text-xs text-green-600 mb-2">Cronograma de Pagamento</div>

                                <div className="mb-3 space-y-1">
                                  {dadosFinanceiros.grupos_parcelas.map((grupo, index) => (
                                    <div key={index} className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded">
                                      Grupo {index + 1}: {grupo.quantidade} parcelas (Peso {grupo.peso})
                                    </div>
                                  ))}
                                </div>

                                <div className="mb-2 text-xs text-green-700">
                                  <strong>1ª Parcela:</strong> {dadosFinanceiros.primeira_parcela_tipo === 'A_VISTA' ? 'À Vista' : `${dadosFinanceiros.primeira_parcela_dias} dias`}
                                </div>

                                <div className="max-h-32 overflow-y-auto space-y-1">
                                  {parcelasCalculadas.slice(0, 8).map((parcela, index) => (
                                    <div key={index} className="flex justify-between text-xs text-green-700">
                                      <span>{parcela.numero}ª Parc</span>
                                      <span>R$ {parcela.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} - {parcela.data.toLocaleDateString('pt-BR')}</span>
                                    </div>
                                  ))}
                                  {parcelasCalculadas.length > 8 && (
                                    <div className="text-xs text-green-600 italic text-center">
                                      ... e mais {parcelasCalculadas.length - 8} parcelas
                                    </div>
                                  )}
                                </div>

                                <div className="mt-2 pt-2 border-t border-green-300">
                                  <div className="flex justify-between text-xs text-green-700 mb-1">
                                    <span>Total de Parcelas:</span>
                                    <span>{parcelasCalculadas.length}</span>
                                  </div>
                                  <div className="flex justify-between text-xs text-green-700 mb-1">
                                    <span>Valor Médio/Parcela:</span>
                                    <span>R$ {(parcelasCalculadas.reduce((s, p) => s + p.valor, 0) / parcelasCalculadas.length).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                                  </div>
                                  <div className="flex justify-between text-xs font-semibold text-green-800">
                                    <span>Valor Líquido Total:</span>
                                    <span>R$ {parcelasCalculadas.reduce((s, p) => s + p.valor, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                                  </div>
                                </div>
                              </div>
                            )
                          })()}

                          {/* Data de Vencimento (Prazo) */}
                          {dadosFinanceiros.modalidade_pagamento === 'PRAZO' && dadosFinanceiros.dias_prazo && (
                            <div className="mt-3 pt-3 border-t border-green-200">
                              <div className="text-xs text-green-600 mb-1">Data de Vencimento</div>
                              <div className="text-sm text-green-700">
                                {(() => {
                                  const dataBase = configuracoesFaturamento?.data_base_calculo === 'DATA_LEILAO' && leilao?.data_leilao
                                    ? new Date(leilao.data_leilao)
                                    : new Date()
                                  const dataVencimento = calcularDataParcelaCavalo(dataBase, parseInt(dadosFinanceiros.dias_prazo) || 0)
                                  return dataVencimento.toLocaleDateString('pt-BR')
                                })()} ({dadosFinanceiros.dias_prazo} dias)
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="text-center text-gray-500">
                          <DollarSign size={24} className="mx-auto mb-2 opacity-50" />
                          <p className="text-sm">Selecione uma modalidade de pagamento para ver o preview</p>
                        </div>
                      </div>
                    )}

                    {leileirosDoLeilaoList.length > 0 && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Leiloeiro</label>
                        <div className="flex items-center gap-3">
                          <select
                            value={dadosFinanceiros.fora_do_leilao ? '' : (dadosFinanceiros.leiloeiro_id || '')}
                            onChange={(e) => setDadosFinanceiros(prev => ({ ...prev, leiloeiro_id: e.target.value ? parseInt(e.target.value) : null }))}
                            disabled={dadosFinanceiros.fora_do_leilao}
                            className="input-rematei flex-1"
                          >
                            <option value="">Sem leiloeiro</option>
                            {leileirosDoLeilaoList.map(ll => (
                              <option key={ll.leiloeiro_id} value={ll.leiloeiro_id}>{ll.nome}</option>
                            ))}
                          </select>
                          <label className="flex items-center gap-1.5 text-sm text-gray-600 whitespace-nowrap cursor-pointer">
                            <input
                              type="checkbox"
                              checked={dadosFinanceiros.fora_do_leilao || false}
                              onChange={(e) => setDadosFinanceiros(prev => ({
                                ...prev,
                                fora_do_leilao: e.target.checked,
                                leiloeiro_id: e.target.checked ? null : prev.leiloeiro_id,
                              }))}
                              className="rounded"
                            />
                            Fora do leilão
                          </label>
                        </div>
                        {dadosFinanceiros.fora_do_leilao && (
                          <p className="text-xs text-amber-600 mt-1">Venda fora do martelo — não gera comissão para o leiloeiro.</p>
                        )}
                      </div>
                    )}

                    {assessoresList.length > 0 && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Assessor</label>
                        <div className="flex items-center gap-3">
                          <select
                            value={dadosFinanceiros.assessor_id || ''}
                            onChange={(e) => {
                              const novoId = e.target.value ? parseInt(e.target.value) : null
                              const assessorSelecionado = assessoresList.find(a => a.id === novoId)
                              setDadosFinanceiros(prev => ({
                                ...prev,
                                assessor_id: novoId,
                                assessor_percentual: novoId
                                  ? (assessorSelecionado?.percentual_padrao ?? prev.assessor_percentual)
                                  : null,
                              }))
                            }}
                            className="input-rematei flex-1"
                          >
                            <option value="">Sem assessor</option>
                            {assessoresList.map(a => (
                              <option key={a.id} value={a.id}>{a.nome}</option>
                            ))}
                          </select>
                          {dadosFinanceiros.assessor_id && (
                            <div className="flex items-center gap-1">
                              <input
                                type="number" step="0.01" min="0" max="100"
                                value={dadosFinanceiros.assessor_percentual ?? ''}
                                onChange={(e) => setDadosFinanceiros(prev => ({ ...prev, assessor_percentual: e.target.value }))}
                                className="w-20 px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                placeholder="%"
                              />
                              <span className="text-sm font-bold text-gray-600">%</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Desconto */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Desconto</label>
                  <div className="flex items-center gap-2">
                    <select
                      ref={setFieldRef('desconto_percentual')}
                      value={dadosFinanceiros.desconto_tipo}
                      onChange={(e) => setDadosFinanceiros({ ...dadosFinanceiros, desconto_tipo: e.target.value, desconto_percentual: '', desconto_valor: '' })}
                      onKeyDown={(e) => handleFieldKeyDown(e, dadosFinanceiros.desconto_tipo ? null : 'modalidade_pagamento')}
                      className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Sem desconto</option>
                      <option value="percentual">% Percentual</option>
                      <option value="valor">R$ Valor fixo</option>
                    </select>
                    {dadosFinanceiros.desconto_tipo === 'percentual' && (
                      <div className="flex items-center gap-1">
                        <input
                          type="number" step="0.01" min="0" max="100"
                          value={dadosFinanceiros.desconto_percentual}
                          onChange={(e) => setDadosFinanceiros({ ...dadosFinanceiros, desconto_percentual: e.target.value })}
                          onKeyDown={(e) => handleFieldKeyDown(e, 'modalidade_pagamento')}
                          className="w-24 px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          placeholder="Ex: 5"
                        />
                        <span className="text-sm font-bold text-gray-600">%</span>
                      </div>
                    )}
                    {dadosFinanceiros.desconto_tipo === 'valor' && (
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-bold text-gray-600">R$</span>
                        <input
                          type="number" step="0.01" min="0"
                          value={dadosFinanceiros.desconto_valor}
                          onChange={(e) => setDadosFinanceiros({ ...dadosFinanceiros, desconto_valor: e.target.value })}
                          onKeyDown={(e) => handleFieldKeyDown(e, 'modalidade_pagamento')}
                          className="w-32 px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          placeholder="Ex: 1000"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </fieldset>
            )}

            {/* Editar rateio de compradores após faturado (fora do fieldset
                disabled — só cavalo, não-defesa) */}
            {jaFaturado && !emDefesa && compradorSelecionado && (
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-sm font-semibold text-amber-900">Rateio de compradores</h4>
                    <p className="text-xs text-amber-700">
                      {compradoresLote.length > 1
                        ? `${compradoresLote.length} compradores neste lote`
                        : 'Comprador único — divida a compra em % se necessário'}
                    </p>
                  </div>
                  {!editandoRateio ? (
                    <button type="button" onClick={() => setEditandoRateio(true)}
                      className="text-sm font-medium text-amber-800 hover:text-amber-900 underline">
                      Editar
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button type="button" disabled={salvandoRateio}
                        onClick={() => { setEditandoRateio(false); carregarLote() }}
                        className="text-sm text-gray-600 hover:text-gray-800">
                        Cancelar
                      </button>
                      <button type="button" disabled={salvandoRateio}
                        onClick={handleSalvarRateio}
                        className="px-3 py-1.5 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50">
                        {salvandoRateio ? 'Salvando...' : 'Salvar rateio'}
                      </button>
                    </div>
                  )}
                </div>
                {editandoRateio && (
                  <>
                    <CompradoresCavaloEditor
                      compradores={compradoresLote}
                      onChange={setCompradoresLote}
                      organizadorId={organizadorId}
                      disabled={salvandoRateio}
                    />
                    <p className="text-xs text-amber-700 mt-2">
                      Ao salvar, a comissão a receber e as parcelas do valor da compra são
                      re-sincronizadas na Gestão automaticamente.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Modal Salvar Meio de Pagamento */}
            {showSalvarMeioPagamento && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
                  <div className="flex items-center justify-between p-5 border-b border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-900">Salvar Meio de Pagamento</h2>
                    <button
                      onClick={() => { setShowSalvarMeioPagamento(false); setNomeMeioPagamento('') }}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <X size={20} className="text-gray-500" />
                    </button>
                  </div>
                  <div className="p-5 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Meio de Pagamento *</label>
                      <input
                        type="text" value={nomeMeioPagamento}
                        onChange={(e) => setNomeMeioPagamento(e.target.value)}
                        className="input-rematei"
                        placeholder="Ex: 5 Duplas + 30 Simples"
                        autoFocus
                      />
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                      <strong>Configuração atual:</strong>
                      <div className="mt-1">
                        1ª Parcela: {dadosFinanceiros.primeira_parcela_tipo === 'A_VISTA' ? 'À Vista' : `${dadosFinanceiros.primeira_parcela_dias} dias`}
                      </div>
                      <div>
                        Grupos: {dadosFinanceiros.grupos_parcelas.map(g => `${g.quantidade}x${g.peso}`).join(', ')}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3 p-5 pt-0">
                    <button
                      type="button"
                      onClick={() => { setShowSalvarMeioPagamento(false); setNomeMeioPagamento('') }}
                      className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={salvarMeioPagamento}
                      className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors font-medium"
                    >
                      Salvar
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Modal Meus Meios de Pagamento */}
            {showMeiosPagamentoModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
                  <div className="flex items-center justify-between p-5 border-b border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-900">Meus Meios de Pagamento</h2>
                    <button onClick={() => setShowMeiosPagamentoModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                      <X size={20} className="text-gray-500" />
                    </button>
                  </div>
                  <div className="p-5">
                    {meiosPagamento.filter(m => m.tipo === 'PARCELADO').length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <p>Nenhum meio de pagamento parcelado salvo ainda.</p>
                        <p className="text-sm mt-1">Configure um parcelamento e clique em "Salvar como modelo" para criar o primeiro.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {meiosPagamento.filter(m => m.tipo === 'PARCELADO').map((meio) => (
                          <div key={meio.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <h3 className="font-medium text-gray-900">{meio.nome}</h3>
                                <div className="text-sm text-gray-600 mt-1">
                                  1ª Parcela: {meio.primeira_parcela_tipo === 'A_VISTA' ? 'À Vista' : `${meio.primeira_parcela_dias} dias`} · Grupos: {meio.grupos_parcelas?.map(g => `${g.quantidade}x${g.peso}`).join(', ')}
                                </div>
                              </div>
                              <div className="flex gap-2 flex-shrink-0">
                                <button
                                  onClick={() => carregarMeioPagamento(meio)}
                                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors text-sm"
                                >
                                  Usar
                                </button>
                                <button
                                  onClick={() => excluirMeioPagamento(meio.id)}
                                  className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors text-sm"
                                >
                                  Excluir
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navegação — sempre visível (inclusive no Financeiro), mesmo padrão do gado */}
      <div className="flex justify-between items-center mt-6">
        <button
          onClick={prevStep}
          disabled={currentStep === 0}
          className={`px-6 py-2 rounded-lg font-medium ${currentStep === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
        >
          Anterior
        </button>

        <div className="flex items-center gap-3 flex-wrap justify-end">
          <button onClick={() => navigate('/lotes')} className="btn-secondary">Cancelar</button>

          {isEditMode ? (
            <>
              {dadosAlterados && !loteFechado && (
                <span className="flex items-center gap-1.5 px-3 py-2 bg-amber-50 border border-amber-300 rounded-lg text-xs font-semibold text-amber-800 whitespace-nowrap">
                  <AlertTriangle size={14} className="flex-shrink-0" /> Salve antes de faturar!
                </span>
              )}
              {stepId === 'financeiro' ? (
                podeFaturar && (
                  <button
                    onClick={handleFaturar}
                    disabled={saving}
                    className="px-4 sm:px-6 py-2 rounded-lg font-bold flex items-center gap-2 transition-all disabled:opacity-50 bg-green-600 hover:bg-green-700 text-white shadow-md ring-2 ring-green-300"
                  >
                    <DollarSign className="w-4 h-4" />
                    {saving ? 'Faturando...' : 'Faturar Lote'}
                  </button>
                )
              ) : (
                <>
                  {currentStep < steps.length - 1 && (
                    <button
                      onClick={nextStep}
                      disabled={!isStepValid(currentStep)}
                      className="btn-primary disabled:opacity-50"
                    >
                      Próximo
                    </button>
                  )}
                  {!loteFechado && dadosAlterados && (
                    <button
                      onClick={handleSalvarLote}
                      disabled={saving}
                      className="px-4 sm:px-6 py-2 rounded-lg font-semibold flex items-center gap-2 transition-all disabled:opacity-50 bg-amber-500 hover:bg-amber-600 text-white ring-2 ring-amber-300 shadow-md"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? 'Salvando...' : 'Salvar Alterações'}
                    </button>
                  )}
                </>
              )}
            </>
          ) : currentStep === steps.length - 1 ? (
            <button onClick={handleSalvarLote} disabled={saving} className="btn-primary">
              <Save className="w-4 h-4 mr-2 inline" />
              {saving ? 'Salvando...' : 'Criar Lote'}
            </button>
          ) : (
            <button
              onClick={nextStep}
              disabled={!isStepValid(currentStep)}
              className="btn-primary disabled:opacity-50"
            >
              Próximo
            </button>
          )}
        </div>
      </div>

      {/* Modal Definir Valor de Venda — fora do ternário acima de propósito:
          precisa renderizar tanto a partir do gate (!valorVendaDefinido) quanto
          de dentro do formulário (reabrir pra corrigir o valor já definido).
          Fica no nível de topo (não dentro do step Financeiro) porque o botão
          que abre esse modal está no header e precisa funcionar em qualquer
          step — antes ficava preso dentro de {stepId === 'financeiro' && (...)}
          e não abria de nenhum outro step. */}
      {showValorVendaModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Definir Valor de Venda</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  Lote {formData.numero_lote} — {animais.length} {animais.length === 1 ? 'animal' : 'animais'}
                </p>
              </div>
              <button onClick={() => { setShowValorVendaModal(false); setValorVendaInput(''); setValorVendaExcecaoPorCabeca(false) }} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {leilao?.regra_faturamento === 'por_parcela' && (
                <label className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={valorVendaExcecaoPorCabeca}
                    onChange={(e) => setValorVendaExcecaoPorCabeca(e.target.checked)}
                    className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-amber-800 font-medium">Exceção: faturar por cabeça neste lote</span>
                </label>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {isPorParcelaLeilao ? 'Valor por Parcela (R$)' : 'Valor por Cabeça (R$)'}
                </label>
                <input
                  ref={valorVendaInputRef}
                  type="number" min="0" step="0.01"
                  value={valorVendaInput}
                  onChange={(e) => setValorVendaInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') confirmarValorVenda() }}
                  placeholder="0,00"
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  autoFocus
                />
              </div>
              {valorVendaInput && parseFloat(valorVendaInput) > 0 && (() => {
                const qtd = animais.length || 1
                const valorUnit = parseFloat(valorVendaInput)
                const qtdParcelas = calcularQtdParcelas()
                const valorTotal = isPorParcelaLeilao ? valorUnit * qtdParcelas * qtd : valorUnit * qtd
                return (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 space-y-1">
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Quantidade</span>
                      <span className="font-medium">{qtd} {qtd === 1 ? 'animal' : 'animais'}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>{isPorParcelaLeilao ? 'Valor por Parcela' : 'Valor unitário'}</span>
                      <span className="font-medium">R$ {valorUnit.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                    </div>
                    {isPorParcelaLeilao && (
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Unidades (c/ peso)</span>
                        <span className="font-medium">{qtdParcelas}x</span>
                      </div>
                    )}
                    <div className="flex justify-between text-base font-semibold text-yellow-800 border-t border-yellow-200 pt-2 mt-1">
                      <span>Valor Total</span>
                      <span>R$ {valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                )
              })()}
            </div>
            <div className="flex gap-3 p-5 pt-0">
              <button
                onClick={() => { setShowValorVendaModal(false); setValorVendaInput(''); setValorVendaExcecaoPorCabeca(false) }}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                disabled={!valorVendaInput || parseFloat(valorVendaInput) <= 0}
                onClick={confirmarValorVenda}
                className="flex-1 px-4 py-3 bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-xl transition-colors font-medium"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Marcar como Defesa — mesmo motivo do de cima: fora do step
          Financeiro pra abrir a partir do botão do header em qualquer step. */}
      {showDefesaModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Marcar como Defesa</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  Lote {formData.numero_lote} — {animais.length} {animais.length === 1 ? 'animal' : 'animais'}
                </p>
              </div>
              <button onClick={() => { setShowDefesaModal(false); setValorDefesa(''); setPreviewDefesa(null) }} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {defesaUsaPercentual && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Comissão</label>
                  <select
                    value={comissaoDefesaId}
                    onChange={(e) => setComissaoDefesaId(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent"
                  >
                    <option value="">Sem comissão (defesa gratuita)</option>
                    {comissoes.map(c => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {defesaPorParcela ? 'Valor por Parcela (R$)' : 'Valor (R$)'}
                </label>
                <input
                  ref={defesaInputRef}
                  type="number" min="0" step="0.01"
                  value={valorDefesa}
                  onChange={(e) => setValorDefesa(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') marcarComoDefesa() }}
                  placeholder="0,00"
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent"
                />
                {defesaPorParcela && (
                  <p className="text-xs text-gray-400 mt-1">
                    Leilão fatura por parcela — multiplica pelas {calcularQtdParcelasLeilao()} parcelas do meio de pagamento padrão.
                  </p>
                )}
              </div>
              {valorDefesa && parseFloat(valorDefesa.replace(',', '.')) > 0 && (
                <div className="bg-red-50 rounded-lg p-3 space-y-1 text-sm border border-red-100">
                  {defesaPorParcela && (
                    <>
                      <div className="flex justify-between text-gray-600">
                        <span>Valor por parcela</span>
                        <span className="font-medium">R$ {parseFloat(valorDefesa.replace(',', '.')).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>Quantidade de parcelas</span>
                        <span className="font-medium">{calcularQtdParcelasLeilao()}</span>
                      </div>
                      <div className="flex justify-between text-gray-600 border-t border-red-200 pt-1 mt-1">
                        <span>Valor Total</span>
                        <span className="font-medium">R$ {valorTotalDefesa().toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </>
                  )}
                  {previewDefesaLoading ? (
                    <p className="text-gray-500">Calculando...</p>
                  ) : previewDefesa ? (
                    <>
                      {previewDefesa.modo === 'percentual' && previewDefesa.comissao_defesa_pct != null && (
                        <div className="flex justify-between text-gray-600">
                          <span>Comissão de defesa</span>
                          <span className="font-medium">{previewDefesa.comissao_defesa_pct}%</span>
                        </div>
                      )}
                      <div className="flex justify-between text-base font-semibold text-red-800 border-t border-red-200 pt-2 mt-1">
                        <span>Valor da Defesa</span>
                        <span>R$ {Number(previewDefesa.valor_defesa || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </>
                  ) : (
                    <p className="text-gray-400">—</p>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-3 p-5 pt-0">
              <button
                onClick={() => { setShowDefesaModal(false); setValorDefesa(''); setPreviewDefesa(null) }}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                disabled={!valorDefesa || parseFloat(valorDefesa.replace(',', '.')) <= 0 || saving}
                onClick={marcarComoDefesa}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-xl transition-colors font-medium"
              >
                {saving ? 'Salvando...' : 'Confirmar Defesa'}
              </button>
            </div>
          </div>
        </div>
      )}

      <NovoClienteModal
        isOpen={showNovoVendedor}
        onClose={() => setShowNovoVendedor(false)}
        organizadorId={organizadorId}
        defaultGrupo="vendedor"
        title="Novo Vendedor"
        onClienteCreated={(cliente) => {
          setVendedoresLote(prev => {
            // Se só há uma linha vazia, preenche ela; senão, adiciona uma nova
            if (prev.length === 1 && !prev[0].vendedor_id) {
              return [{ vendedor_id: cliente.id, vendedor_nome: cliente.nome, percentual: 100 }]
            }
            return [...prev, { vendedor_id: cliente.id, vendedor_nome: cliente.nome, percentual: '' }]
          })
          setShowNovoVendedor(false)
        }}
      />

      <NovoClienteModal
        isOpen={showNovoComprador}
        onClose={() => setShowNovoComprador(false)}
        organizadorId={organizadorId}
        defaultGrupo="comprador"
        title="Novo Comprador"
        onClienteCreated={(cliente) => {
          selecionarComprador(cliente)
          setShowNovoComprador(false)
        }}
      />

      <NovaComissaoModal
        isOpen={showNovaComissaoModal}
        onClose={() => setShowNovaComissaoModal(false)}
        onSave={async (dadosComissao) => {
          try {
            const resp = await comissoesService.criar(organizadorId, dadosComissao)
            const novaComissao = resp?.data || resp
            if (novaComissao && novaComissao.id) {
              setComissoes(prev => [...prev, novaComissao])
              setDadosFinanceiros(prev => ({ ...prev, comissao_id: novaComissao.id.toString() }))
              success('Comissão criada com sucesso!')
            }
            setShowNovaComissaoModal(false)
          } catch (err) {
            console.error('Erro ao criar comissão:', err)
            error('Erro ao criar comissão')
          }
        }}
      />

      {/* Popup pós-salvamento (só no cadastro): pergunta se quer cadastrar outro lote */}
      {loteSalvoModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              Lote {loteSalvoModal.numero} salvo com sucesso!
            </h2>
            <p className="text-sm text-gray-500 mt-1 mb-5">Deseja cadastrar um novo lote agora?</p>
            <div className="flex gap-3">
              <button
                onClick={() => { const id = loteSalvoModal.id; setLoteSalvoModal(null); navigate(`/lotes/editar-cavalo/${id}`) }}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
              >
                Ver este lote
              </button>
              <button
                onClick={resetParaNovoLote}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors font-medium"
              >
                Criar novo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
